import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import WhatsAppSettingsPanel from '@/pages/WhatsAppSettingsPanel';
import {
  FileText,
  MessageSquare,
  Settings,
  Save,
  Loader2,
  Trash2,
  CheckCircle,
  Clock,
  Mail,
  Users,
  User,
  Building2,
  Edit,
  Plus,
  Shield,
  ShieldCheck,
  ShieldX,
  Wallet,
  X,
  Coins,
  Calculator,
  Play,
  ChevronDown,
  ChevronUp,
  Upload,
  Image,
  Globe,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  MenuIcon,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  UserPlus,
  KeyRound,
  Ban,
  Search,
  MessageCircle,
  DollarSign,
  Briefcase,
  ArrowDownToLine,
  CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FlagImg = ({ code, className = "w-4 h-auto inline rounded-sm" }) => {
  const codes = { ja: 'jp', en: 'us', pt: 'br' };
  return <img src={`https://flagcdn.com/w20/${codes[code] || code}.png`} alt="" className={className} />;
};

const AdminPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');

  // Reset user search when switching tabs
  const handleTabChange = (tab) => {
    if (tab !== 'users') {
      setUserSearch('');
      setUserRoleFilter('all');
      setUserKycFilter('all');
      setUserStatusFilter('all');
    }
    setActiveTab(tab);
  };
  const [pages, setPages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [funds, setFunds] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [distributionBatches, setDistributionBatches] = useState([]);
  const [kycSubmissions, setKycSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditCategory, setAuditCategory] = useState('all');
  const [auditPage, setAuditPage] = useState(0);
  const [loadingAudit, setLoadingAudit] = useState(false);
  
  // Edit dialogs
  const [editingUser, setEditingUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingFund, setEditingFund] = useState(null);
  const [fundDialogOpen, setFundDialogOpen] = useState(false);
  const [isNewFund, setIsNewFund] = useState(false);
  const [uploadingFundImage, setUploadingFundImage] = useState(false);
  
  // User management
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userKycFilter, setUserKycFilter] = useState('all');
  const [userStatusFilter, setUserStatusFilter] = useState('all');
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', role: 'user' });
  const [userDetailDialogOpen, setUserDetailDialogOpen] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Deposit methods management
  const [depositMethods, setDepositMethods] = useState({
    wise: { account_name: '', account_email: '', currency: 'JPY', notes: '' },
    prc_remit: { account_name: '', account_id: '', currency: 'JPY', notes: '' }
  });
  const [savingDepositMethods, setSavingDepositMethods] = useState(false);
  
  // Distribution dialog
  const [distributionDialogOpen, setDistributionDialogOpen] = useState(false);
  const [distributionForm, setDistributionForm] = useState({
    fund_id: '',
    distribution_date: new Date().toISOString().split('T')[0],
    gross_revenue: '',
    costs: '',
    notes: ''
  });
  const [distributionPreview, setDistributionPreview] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [executing, setExecuting] = useState(false);

  // Pages inline editing
  const [expandedPage, setExpandedPage] = useState(null);
  const [pageEdits, setPageEdits] = useState({});
  const [savingPage, setSavingPage] = useState(null);

  // Logo
  const [logoInfo, setLogoInfo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef(null);

  // Contact management
  const [expandedContact, setExpandedContact] = useState(null);
  const [contactNotes, setContactNotes] = useState({});

  // Company info (structured about)
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '', company_name_en: '', company_name_pt: '',
    representative: '', representative_en: '', representative_pt: '',
    established: '',
    address: '', address_en: '', address_pt: '',
    business: '', business_en: '', business_pt: '',
    capital: '', capital_en: '', capital_pt: '',
    phone: '', email: '',
    directors: [],
    notes: '', notes_en: '', notes_pt: ''
  });
  const [savingCompany, setSavingCompany] = useState(false);

  // Structured pages (terms, privacy, risk)
  const [structuredPages, setStructuredPages] = useState({ terms: [], privacy: [], risk: [] });
  const [savingStructured, setSavingStructured] = useState(null);

  // Menu management
  const [menuItems, setMenuItems] = useState([]);
  const [savingMenu, setSavingMenu] = useState(false);

  // Custom pages
  const [customPages, setCustomPages] = useState([]);
  const [customPageSections, setCustomPageSections] = useState({});
  const [newPageDialogOpen, setNewPageDialogOpen] = useState(false);
  const [newPageForm, setNewPageForm] = useState({ slug: '', title_ja: '', title_en: '', title_pt: '' });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pagesRes, contactsRes, usersRes, fundsRes, withdrawalsRes, distRes, kycRes, logoRes, companyRes, termsRes, privacyRes, riskRes, menuRes, customPagesRes, depositsRes, depositMethodsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/pages`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/contacts`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/funds`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/withdrawals`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/distributions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/kyc`, { withCredentials: true }),
        axios.get(`${API_URL}/api/site/logo-info`).catch(() => ({ data: { has_logo: false } })),
        axios.get(`${API_URL}/api/admin/company-info`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/admin/structured-page/terms`, { withCredentials: true }).catch(() => ({ data: { sections: [] } })),
        axios.get(`${API_URL}/api/admin/structured-page/privacy`, { withCredentials: true }).catch(() => ({ data: { sections: [] } })),
        axios.get(`${API_URL}/api/admin/structured-page/risk`, { withCredentials: true }).catch(() => ({ data: { sections: [] } })),
        axios.get(`${API_URL}/api/admin/menu`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API_URL}/api/admin/custom-pages`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/deposits`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/deposit-methods`, { withCredentials: true }).catch(() => ({ data: { wise: {}, prc_remit: {} } }))
      ]);
      setPages(pagesRes.data);
      setContacts(contactsRes.data);
      setUsers(usersRes.data);
      setFunds(fundsRes.data);
      setWithdrawals(withdrawalsRes.data);
      setDeposits(depositsRes.data || []);
      const dm = depositMethodsRes.data || {};
      setDepositMethods({
        wise: { account_name: '', account_email: '', currency: 'JPY', notes: '', ...dm.wise },
        prc_remit: { account_name: '', account_id: '', currency: 'JPY', notes: '', ...dm.prc_remit }
      });
      setDistributionBatches(distRes.data);
      setKycSubmissions(kycRes.data);
      setLogoInfo(logoRes.data);
      const ci = companyRes.data || {};
      setCompanyInfo(prev => ({ ...prev, ...ci }));
      setStructuredPages({
        terms: termsRes.data?.sections || [],
        privacy: privacyRes.data?.sections || [],
        risk: riskRes.data?.sections || []
      });
      setMenuItems(menuRes.data?.items || []);
      const cp = customPagesRes.data || [];
      setCustomPages(cp);
      // Fetch sections for each custom page
      const cpSections = {};
      for (const p of cp) {
        try {
          const r = await axios.get(`${API_URL}/api/admin/structured-page/${p.slug}`, { withCredentials: true });
          cpSections[p.slug] = r.data?.sections || [];
        } catch { cpSections[p.slug] = []; }
      }
      setCustomPageSections(cpSections);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('admin.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  // Separate user fetch with filters
  const fetchUsers = async (search = userSearch, role = userRoleFilter, kyc = userKycFilter, status = userStatusFilter) => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (role !== 'all') params.append('role_filter', role);
      if (kyc !== 'all') params.append('kyc_filter', kyc);
      if (status !== 'all') params.append('status_filter', status);
      const res = await axios.get(`${API_URL}/api/admin/users?${params}`, { withCredentials: true });
      setUsers(res.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(userSearch, userRoleFilter, userKycFilter, userStatusFilter);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearch, userRoleFilter, userKycFilter, userStatusFilter]);

  // User detail fetch
  const fetchUserDetail = async (userId) => {
    setLoadingUserDetail(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      setUserDetail(res.data);
    } catch { toast.error(t('admin.userFetchError')); }
    finally { setLoadingUserDetail(false); }
  };

  // Create user handler
  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error(t('admin.fillAllFields'));
      return;
    }
    if (newUser.password.length < 8) {
      toast.error(t('admin.passwordMin'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, newUser, { withCredentials: true });
      toast.success(t('admin.userCreated'));
      setCreateUserDialogOpen(false);
      setNewUser({ email: '', name: '', password: '', role: 'user' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.createFailed'));
    } finally { setSaving(false); }
  };

  // Reset password handler
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error(t('admin.passwordMin'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${resetPasswordUser.id}/reset-password`, { new_password: newPassword }, { withCredentials: true });
      toast.success(t('admin.passwordResetDone'));
      setResetPasswordDialogOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.resetFailed'));
    } finally { setSaving(false); }
  };

  // Toggle active handler
  const handleToggleActive = async (userItem) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/users/${userItem.id}/toggle-active`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.operationFailed'));
    }
  };

  // Audit log fetching
  const fetchAuditLogs = async (cat = auditCategory, page = auditPage) => {
    setLoadingAudit(true);
    try {
      const params = new URLSearchParams({ limit: '30', skip: String(page * 30) });
      if (cat && cat !== 'all') params.append('category', cat);
      const res = await axios.get(`${API_URL}/api/admin/audit-logs?${params}`, { withCredentials: true });
      setAuditLogs(res.data.logs || []);
      setAuditTotal(res.data.total || 0);
    } catch { setAuditLogs([]); }
    finally { setLoadingAudit(false); }
  };

  useEffect(() => {
    if (activeTab === 'audit') fetchAuditLogs(auditCategory, auditPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, auditCategory, auditPage]);

  // Page handlers (inline)
  const handleTogglePage = (page) => {
    if (expandedPage === page.slug) {
      setExpandedPage(null);
    } else {
      setExpandedPage(page.slug);
      setPageEdits({
        title: page.title || '',
        title_en: page.title_en || '',
        title_pt: page.title_pt || '',
        content: page.content || '',
        content_en: page.content_en || '',
        content_pt: page.content_pt || ''
      });
    }
  };

  const handleSavePageInline = async (slug) => {
    setSavingPage(slug);
    try {
      await axios.put(
        `${API_URL}/api/admin/pages/${slug}`,
        pageEdits,
        { withCredentials: true }
      );
      toast.success(t('admin.saved'));
      setExpandedPage(null);
      fetchData();
    } catch (error) {
      toast.error(t('admin.saveFailed'));
    } finally {
      setSavingPage(null);
    }
  };

  // Logo handlers
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/api/admin/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('admin.logoUploaded'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.uploadFailed'));
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API_URL}/api/admin/logo`, { withCredentials: true });
      toast.success(t('admin.logoDeleted'));
      setLogoInfo({ has_logo: false });
      fetchData();
    } catch (error) {
      toast.error(t('admin.deleteFailed'));
    }
  };

  // Company info handlers
  const handleSaveCompanyInfo = async () => {
    setSavingCompany(true);
    try {
      await axios.put(`${API_URL}/api/admin/company-info`, companyInfo, { withCredentials: true });
      toast.success(t('admin.companySaved'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.saveFailed'));
    } finally {
      setSavingCompany(false);
    }
  };

  const addDirector = () => {
    setCompanyInfo({
      ...companyInfo,
      directors: [...companyInfo.directors, { name: '', name_en: '', name_pt: '', title: '', title_en: '', title_pt: '' }]
    });
  };

  const updateDirector = (index, field, value) => {
    const updated = [...companyInfo.directors];
    updated[index] = { ...updated[index], [field]: value };
    setCompanyInfo({ ...companyInfo, directors: updated });
  };

  const removeDirector = (index) => {
    setCompanyInfo({
      ...companyInfo,
      directors: companyInfo.directors.filter((_, i) => i !== index)
    });
  };

  const handleDirectorPhotoUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('index', index);
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/director-photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(t('admin.photoUploaded'));
      const updated = [...companyInfo.directors];
      updated[index] = { ...updated[index], photo_id: res.data.photo_id };
      setCompanyInfo({ ...companyInfo, directors: updated });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.uploadFailed'));
    }
  };

  const handleDeleteDirectorPhoto = async (index) => {
    try {
      await axios.delete(`${API_URL}/api/admin/director-photo/${index}`, { withCredentials: true });
      toast.success(t('admin.photoDeleted'));
      const updated = [...companyInfo.directors];
      updated[index] = { ...updated[index], photo_id: null, photo_filename: null };
      setCompanyInfo({ ...companyInfo, directors: updated });
    } catch (error) {
      toast.error(t('admin.deleteFailed'));
    }
  };

  // Structured page handlers
  const addSection = (slug) => {
    setStructuredPages(prev => ({
      ...prev,
      [slug]: [...prev[slug], { heading: '', heading_en: '', heading_pt: '', content: '', content_en: '', content_pt: '' }]
    }));
  };

  const updateSection = (slug, index, field, value) => {
    setStructuredPages(prev => {
      const updated = [...prev[slug]];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [slug]: updated };
    });
  };

  const removeSection = (slug, index) => {
    setStructuredPages(prev => ({
      ...prev,
      [slug]: prev[slug].filter((_, i) => i !== index)
    }));
  };

  const handleSaveStructuredPage = async (slug) => {
    setSavingStructured(slug);
    try {
      await axios.put(
        `${API_URL}/api/admin/structured-page/${slug}`,
        { sections: structuredPages[slug] },
        { withCredentials: true }
      );
      toast.success(t('admin.saved'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.saveFailed'));
    } finally {
      setSavingStructured(null);
    }
  };

  const structuredPageConfig = {
    terms: { title: t('admin.terms'), icon: <FileText className="h-5 w-5" />, slug: 'terms' },
    privacy: { title: t('admin.privacy'), icon: <Shield className="h-5 w-5" />, slug: 'privacy' },
    risk: { title: t('admin.risk'), icon: <AlertTriangle className="h-5 w-5" />, slug: 'risk' }
  };

  // Menu handlers
  const addMenuItem = () => {
    setMenuItems([...menuItems, { label: '', label_en: '', label_pt: '', url: '/', visible: true, order: menuItems.length }]);
  };

  const updateMenuItem = (index, field, value) => {
    const updated = [...menuItems];
    updated[index] = { ...updated[index], [field]: value };
    setMenuItems(updated);
  };

  const removeMenuItem = (index) => {
    setMenuItems(menuItems.filter((_, i) => i !== index).map((item, i) => ({ ...item, order: i })));
  };

  const moveMenuItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= menuItems.length) return;
    const updated = [...menuItems];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setMenuItems(updated.map((item, i) => ({ ...item, order: i })));
  };

  const handleSaveMenu = async () => {
    setSavingMenu(true);
    try {
      await axios.put(`${API_URL}/api/admin/menu`, { items: menuItems }, { withCredentials: true });
      toast.success(t('admin.menuSaved'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.saveFailed'));
    } finally {
      setSavingMenu(false);
    }
  };

  // Custom page handlers
  const handleCreateCustomPage = async () => {
    if (!newPageForm.slug || !newPageForm.title_ja) {
      toast.error(t('admin.slugRequired'));
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/custom-pages`, newPageForm, { withCredentials: true });
      toast.success(t('admin.pageCreated'));
      setNewPageDialogOpen(false);
      setNewPageForm({ slug: '', title_ja: '', title_en: '', title_pt: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.createFailed'));
    }
  };

  const handleDeleteCustomPage = async (slug) => {
    try {
      await axios.delete(`${API_URL}/api/admin/custom-pages/${slug}`, { withCredentials: true });
      toast.success(t('admin.pageDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.deleteFailed'));
    }
  };

  const addCustomSection = (slug) => {
    setCustomPageSections(prev => ({
      ...prev,
      [slug]: [...(prev[slug] || []), { heading: '', heading_en: '', heading_pt: '', content: '', content_en: '', content_pt: '' }]
    }));
  };

  const updateCustomSection = (slug, index, field, value) => {
    setCustomPageSections(prev => {
      const updated = [...(prev[slug] || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [slug]: updated };
    });
  };

  const removeCustomSection = (slug, index) => {
    setCustomPageSections(prev => ({
      ...prev,
      [slug]: (prev[slug] || []).filter((_, i) => i !== index)
    }));
  };

  const handleSaveCustomPage = async (slug) => {
    setSavingStructured(slug);
    try {
      await axios.put(
        `${API_URL}/api/admin/structured-page/${slug}`,
        { sections: customPageSections[slug] || [] },
        { withCredentials: true }
      );
      toast.success(t('admin.saved'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.saveFailed'));
    } finally {
      setSavingStructured(null);
    }
  };

  // Contact handlers
  const handleUpdateContactStatus = async (contactId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/contacts/${contactId}/status?status=${status}`,
        {},
        { withCredentials: true }
      );
      toast.success(t('admin.statusUpdated'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.updateFailed'));
    }
  };

  const handleSaveContactNotes = async (contactId) => {
    const notes = contactNotes[contactId];
    if (notes === undefined) return;
    try {
      await axios.put(
        `${API_URL}/api/admin/contacts/${contactId}/notes`,
        { admin_notes: notes },
        { withCredentials: true }
      );
      toast.success(t('admin.noteSaved'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.saveFailed'));
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/contacts/${contactId}`,
        { withCredentials: true }
      );
      toast.success(t('admin.contactDeleted'));
      if (expandedContact === contactId) setExpandedContact(null);
      fetchData();
    } catch (error) {
      toast.error(t('admin.deleteFailed'));
    }
  };

  const toggleContact = (contact) => {
    if (expandedContact === contact.id) {
      setExpandedContact(null);
    } else {
      setExpandedContact(contact.id);
      setContactNotes({ ...contactNotes, [contact.id]: contact.admin_notes || '' });
    }
  };

  // User handlers
  const handleEditUser = (userItem) => {
    setEditingUser({ ...userItem });
    setUserDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${editingUser.id}`,
        {
          name: editingUser.name,
          role: editingUser.role,
          is_kyc_verified: editingUser.is_kyc_verified,
          kyc_status: editingUser.kyc_status
        },
        { withCredentials: true }
      );
      toast.success(t('admin.userUpdatedMsg'));
      setUserDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(t('admin.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${userId}`,
        { withCredentials: true }
      );
      toast.success(t('admin.userDeletedMsg'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.deleteFailed'));
    }
  };

  // Fund handlers
  const handleNewFund = () => {
    setEditingFund({
      name: '',
      name_en: '',
      name_pt: '',
      description: '',
      description_en: '',
      description_pt: '',
      target_yield: 8.0,
      risk_level: 'medium',
      min_investment: 100000,
      status: 'active',
      image_url: '',
      property_type: '',
      location: '',
      is_guaranteed: false,
      guarantee_type: 'none',
      guarantee_rate: null,
      guarantee_period: '',
      guarantee_conditions: '',
      guarantee_conditions_en: '',
      guarantee_conditions_pt: ''
    });
    setIsNewFund(true);
    setFundDialogOpen(true);
  };

  const handleEditFund = (fund) => {
    setEditingFund({ ...fund });
    setIsNewFund(false);
    setFundDialogOpen(true);
  };

  const handleSaveFund = async () => {
    if (!editingFund) return;
    setSaving(true);
    try {
      if (isNewFund) {
        await axios.post(
          `${API_URL}/api/admin/funds`,
          editingFund,
          { withCredentials: true }
        );
        toast.success(t('admin.fundCreatedMsg'));
      } else {
        await axios.put(
          `${API_URL}/api/admin/funds/${editingFund.id}`,
          editingFund,
          { withCredentials: true }
        );
        toast.success(t('admin.fundUpdatedMsg'));
      }
      setFundDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(t('admin.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFund = async (fundId) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/funds/${fundId}`,
        { withCredentials: true }
      );
      toast.success(t('admin.fundDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.deleteFailed'));
    }
  };

  const handleFundImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFundImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/api/admin/funds/upload-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEditingFund({ ...editingFund, image_url: `${API_URL}${res.data.url}` });
      toast.success(t('admin.photoUploaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.uploadFailed'));
    } finally {
      setUploadingFundImage(false);
      e.target.value = '';
    }
  };

  const getPageTitle = (slug) => {
    const titles = {
      about: t('admin.about'),
      terms: t('admin.terms'),
      privacy: t('admin.privacy'),
      risk: t('admin.risk')
    };
    return titles[slug] || slug;
  };

  const getPageIcon = (slug) => {
    const icons = {
      about: <Building2 className="h-5 w-5" />,
      terms: <FileText className="h-5 w-5" />,
      privacy: <Shield className="h-5 w-5" />,
      risk: <AlertTriangle className="h-5 w-5" />
    };
    return icons[slug] || <FileText className="h-5 w-5" />;
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500/15 text-blue-400 border-blue-400/40',
      read: 'bg-yellow-500/15 text-yellow-400 border-yellow-400/40',
      replied: 'bg-emerald-500/15 text-emerald-400 border-emerald-400/40'
    };
    const labels = { new: t('admin.statusNew'), read: t('admin.statusRead'), replied: t('admin.statusReplied') };
    return <Badge className={`${styles[status] || styles.new} border`}>{labels[status] || status}</Badge>;
  };

  const getKYCBadge = (isVerified, status) => {
    if (isVerified) {
      return <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-400/40 border">{t('admin.verifiedBadge')}</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-yellow-500/15 text-yellow-400 border-yellow-400/40 border">{t('admin.pendingBadge')}</Badge>;
    }
    return <Badge className="bg-zinc-500/15 text-zinc-400 border-zinc-400/40 border">{t('admin.unverifiedBadge')}</Badge>;
  };

  const getRoleBadge = (role) => {
    if (role === 'admin') {
      return <Badge className="bg-purple-500/15 text-purple-400 border-purple-400/40 border">{t('admin.roleAdmin')}</Badge>;
    }
    return <Badge className="bg-zinc-500/15 text-zinc-300 border-zinc-400/40 border">{t('admin.roleUser')}</Badge>;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getWithdrawalStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-400/40',
      approved: 'bg-blue-500/15 text-blue-400 border-blue-400/40',
      completed: 'bg-emerald-500/15 text-emerald-400 border-emerald-400/40',
      rejected: 'bg-red-500/15 text-red-400 border-red-400/40'
    };
    const labels = { pending: t('admin.statusPendingW'), approved: t('admin.statusApprovedW'), completed: t('admin.statusCompleted'), rejected: t('admin.statusRejectedW') };
    return <Badge className={`${styles[status] || styles.pending} border`}>{labels[status] || status}</Badge>;
  };

  const handleUpdateWithdrawalStatus = async (withdrawalId, status) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/withdrawals/${withdrawalId}`,
        { status },
        { withCredentials: true }
      );
      toast.success(t('admin.statusUpdated'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.updateFailed'));
    }
  };

  const handleUpdateDepositStatus = async (depositId, status, adminNotes = null) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/deposits/${depositId}`,
        { status, admin_notes: adminNotes },
        { withCredentials: true }
      );
      toast.success(status === 'approved' ? t('admin.depositApproved') : t('admin.depositRejected'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.updateFailed'));
    }
  };

  const getDepositStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-400/40',
      approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-400/40',
      rejected: 'bg-red-500/15 text-red-400 border-red-400/40'
    };
    const labels = { pending: t('admin.statusPendingD'), approved: t('admin.statusApprovedD'), rejected: t('admin.statusRejectedD') };
    return <Badge className={`${styles[status] || styles.pending} border`}>{labels[status] || status}</Badge>;
  };

  const handleSaveDepositMethods = async () => {
    setSavingDepositMethods(true);
    try {
      await axios.put(`${API_URL}/api/admin/deposit-methods`, depositMethods, { withCredentials: true });
      toast.success(t('admin.depositMethodsSaved'));
    } catch (error) {
      toast.error(t('admin.saveFailed'));
    } finally {
      setSavingDepositMethods(false);
    }
  };

  // Distribution handlers
  const handleOpenDistributionDialog = () => {
    setDistributionForm({
      fund_id: funds.length > 0 ? funds[0].id : '',
      distribution_date: new Date().toISOString().split('T')[0],
      gross_revenue: '',
      costs: '',
      notes: ''
    });
    setDistributionPreview(null);
    setDistributionDialogOpen(true);
  };

  const handleCalculateDistribution = async () => {
    if (!distributionForm.fund_id || !distributionForm.gross_revenue) {
      toast.error(t('admin.enterFundAndRevenue'));
      return;
    }
    
    setCalculating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/distributions/calculate`,
        {
          fund_id: distributionForm.fund_id,
          distribution_date: distributionForm.distribution_date,
          gross_revenue: parseFloat(distributionForm.gross_revenue),
          costs: parseFloat(distributionForm.costs) || 0,
          notes: distributionForm.notes
        },
        { withCredentials: true }
      );
      setDistributionPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.calcFailed'));
    } finally {
      setCalculating(false);
    }
  };

  const handleExecuteDistribution = async () => {
    if (!distributionPreview) return;
    
    setExecuting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/distributions/execute`,
        {
          fund_id: distributionForm.fund_id,
          distribution_date: distributionForm.distribution_date,
          gross_revenue: parseFloat(distributionForm.gross_revenue),
          costs: parseFloat(distributionForm.costs) || 0,
          notes: distributionForm.notes
        },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setDistributionDialogOpen(false);
      setDistributionPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.execFailed'));
    } finally {
      setExecuting(false);
    }
  };

  // KYC handlers
  const handleReviewKYC = async (submissionId, action, rejectionReason) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/kyc/${submissionId}`,
        { action, rejection_reason: rejectionReason },
        { withCredentials: true }
      );
      toast.success(action === 'approve' ? t('admin.kycApproved') : t('admin.kycRejected'));
      fetchData();
    } catch (error) {
      toast.error(t('admin.updateFailed'));
    }
  };

  const getKYCSubmissionBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500/15 text-amber-400 border-amber-400/40',
      approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-400/40',
      rejected: 'bg-red-500/15 text-red-400 border-red-400/40'
    };
    const labels = { pending: t('admin.kycPending'), approved: t('admin.kycApprovedLabel'), rejected: t('admin.kycRejectedLabel') };
    return <Badge className={`${styles[status] || styles.pending} border`}>{labels[status] || status}</Badge>;
  };

  const getDocTypeName = (type) => {
    const names = { passport: t('admin.docPassport'), driver_license: t('admin.docLicense'), id_card: t('admin.docIdCard') };
    return names[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Skeleton className="h-10 w-48 mb-8 bg-[#141414]" />
          <Skeleton className="h-96 w-full bg-[#141414]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Settings className="h-8 w-8 text-white" />
            <h1 className="font-heading text-3xl text-white">{t('admin.dashboard')}</h1>
          </div>
          <a href="/br-invest" className="inline-flex items-center gap-2 bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 px-4 py-2 rounded-sm text-sm font-medium transition-colors border border-emerald-500/20" data-testid="admin-br-invest-link">
            <svg viewBox="0 0 20 14" className="w-5 h-3.5 shrink-0"><rect width="20" height="14" rx="1" fill="#009739"/><polygon points="10,1.5 18.5,7 10,12.5 1.5,7" fill="#FEDD00"/><circle cx="10" cy="7" r="3" fill="#012169"/></svg>
            {t('br.title')}
          </a>
          <a href="/contracts" className="inline-flex items-center gap-2 bg-amber-600/20 text-amber-400 hover:bg-amber-600/30 px-4 py-2 rounded-sm text-sm font-medium transition-colors border border-amber-500/20" data-testid="admin-contracts-link">
            <FileText className="w-4 h-4" />
            {t('contract.title')}
          </a>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="bg-[#111111] border border-white/15 mb-6 flex-wrap h-auto gap-1 p-1.5">
            <TabsTrigger 
              value="users" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-users"
            >
              <Users className="h-4 w-4 mr-2" />
              {t('admin.users')}
            </TabsTrigger>
            <TabsTrigger 
              value="funds" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-funds"
            >
              <Building2 className="h-4 w-4 mr-2" />
              {t('admin.funds')}
            </TabsTrigger>
            <TabsTrigger 
              value="pages" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-pages"
            >
              <FileText className="h-4 w-4 mr-2" />
              {t('admin.pages')}
            </TabsTrigger>
            <TabsTrigger 
              value="menu" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-menu"
            >
              <MenuIcon className="h-4 w-4 mr-2" />
              {t('admin.menus')}
            </TabsTrigger>
            <TabsTrigger 
              value="contacts" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-contacts"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('admin.contacts')}
              {contacts.filter(c => c.status === 'new').length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {contacts.filter(c => c.status === 'new').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="withdrawals" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-withdrawals"
            >
              <Wallet className="h-4 w-4 mr-2" />
              {t('admin.withdrawals')}
              {withdrawals.filter(w => w.status === 'pending').length > 0 && (
                <span className="ml-2 bg-amber-500 text-black text-xs px-1.5 py-0.5 rounded-full">
                  {withdrawals.filter(w => w.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="deposits" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-deposits"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {t('admin.deposits')}
              {deposits.filter(d => d.status === 'pending').length > 0 && (
                <span className="ml-2 bg-emerald-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {deposits.filter(d => d.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="distributions" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-distributions"
            >
              <Coins className="h-4 w-4 mr-2" />
              {t('admin.distributions')}
            </TabsTrigger>
            <TabsTrigger 
              value="kyc" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400"
              data-testid="admin-tab-kyc"
            >
              <Shield className="h-4 w-4 mr-2" />
              {t('admin.kyc')}
              {kycSubmissions.filter(k => k.status === 'pending').length > 0 && (
                <span className="ml-2 bg-amber-500 text-black text-xs px-1.5 py-0.5 rounded-full">
                  {kycSubmissions.filter(k => k.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="audit" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-audit"
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              {t('admin.auditLogs')}
            </TabsTrigger>
            <TabsTrigger 
              value="whatsapp" 
              className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"
              data-testid="admin-tab-whatsapp"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="card-presage rounded-sm">
              <CardContent className="p-6">
                {/* Header with Create button */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <h3 className="text-white font-semibold">{t('admin.users')} ({users.length})</h3>
                  <Button
                    onClick={() => { setNewUser({ email: '', name: '', password: '', role: 'user' }); setCreateUserDialogOpen(true); }}
                    className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                    data-testid="create-user-btn"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    {t('admin.createUser')}
                  </Button>
                </div>

                {/* Search & Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                    <Input
                      placeholder={t("admin.searchUser")}
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white rounded-sm pl-10"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm w-36" data-testid="user-role-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1C] border-white/10">
                      <SelectItem value="all" className="text-zinc-300">{t('admin.allRoles')}</SelectItem>
                      <SelectItem value="admin" className="text-zinc-300">{t('admin.roleAdmin')}</SelectItem>
                      <SelectItem value="user" className="text-zinc-300">{t('admin.roleUser')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={userKycFilter} onValueChange={setUserKycFilter}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm w-36" data-testid="user-kyc-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1C] border-white/10">
                      <SelectItem value="all" className="text-zinc-300">{t('admin.allKyc')}</SelectItem>
                      <SelectItem value="verified" className="text-zinc-300">{t('admin.verified')}</SelectItem>
                      <SelectItem value="pending" className="text-zinc-300">{t('admin.pending')}</SelectItem>
                      <SelectItem value="not_started" className="text-zinc-300">{t('admin.notStarted')}</SelectItem>
                      <SelectItem value="rejected" className="text-zinc-300">{t('admin.rejected')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm w-36" data-testid="user-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1C] border-white/10">
                      <SelectItem value="all" className="text-zinc-300">{t('admin.allStatus')}</SelectItem>
                      <SelectItem value="active" className="text-zinc-300">{t('admin.active')}</SelectItem>
                      <SelectItem value="inactive" className="text-zinc-300">{t('admin.inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Users Table */}
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="users-table">
                    <thead>
                      <tr className="border-b border-white/15">
                        <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.name')}</th>
                        <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.email')}</th>
                        <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.role')}</th>
                        <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.kycLabel')}</th>
                        <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.status')}</th>
                        <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.registeredAt')}</th>
                        <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((userItem) => (
                        <tr key={userItem.id} className={`border-b border-white/5 hover:bg-white/[0.07] transition-colors ${!userItem.is_active ? 'opacity-50' : ''}`} data-testid={`user-row-${userItem.id}`}>
                          <td className="py-4 px-2 text-white font-medium">{userItem.name}</td>
                          <td className="py-4 px-2 text-zinc-300 text-sm">{userItem.email}</td>
                          <td className="py-4 px-2">{getRoleBadge(userItem.role)}</td>
                          <td className="py-4 px-2">{getKYCBadge(userItem.is_kyc_verified, userItem.kyc_status)}</td>
                          <td className="py-4 px-2">
                            {userItem.is_active !== false ? (
                              <Badge className="bg-emerald-500/10 text-emerald-400 border-0">{t('admin.active')}</Badge>
                            ) : (
                              <Badge className="bg-red-500/10 text-red-400 border-0">{t('admin.inactive')}</Badge>
                            )}
                          </td>
                          <td className="py-4 px-2 text-zinc-400 text-sm">
                            {new Date(userItem.created_at).toLocaleDateString('ja-JP')}
                          </td>
                          <td className="py-4 px-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-zinc-400 hover:text-white rounded-sm"
                                onClick={() => { fetchUserDetail(userItem.id); setUserDetailDialogOpen(true); }}
                                data-testid={`user-detail-btn-${userItem.id}`}
                                title={t('admin.detailTitle')}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-zinc-400 hover:text-white rounded-sm"
                                onClick={() => handleEditUser(userItem)}
                                title={t('admin.editTitle')}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-white/20 text-zinc-400 hover:text-white rounded-sm"
                                onClick={() => { setResetPasswordUser(userItem); setNewPassword(''); setShowNewPassword(false); setResetPasswordDialogOpen(true); }}
                                data-testid={`user-reset-pw-btn-${userItem.id}`}
                                title={t('admin.resetPwTitle')}
                              >
                                <KeyRound className="h-4 w-4" />
                              </Button>
                              {userItem.id !== user.id && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={`rounded-sm ${userItem.is_active !== false ? 'border-amber-500/30 text-amber-500 hover:bg-amber-500/10' : 'border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10'}`}
                                    onClick={() => handleToggleActive(userItem)}
                                    data-testid={`user-toggle-active-${userItem.id}`}
                                    title={userItem.is_active !== false ? t('admin.disableTitle') : t('admin.enableTitle')}
                                  >
                                    {userItem.is_active !== false ? <Ban className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-sm"
                                        title={t('admin.deleteTitle')}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent className="bg-[#141414] border-white/10">
                                      <AlertDialogHeader>
                                        <AlertDialogTitle className="text-white">{t('admin.deleteUser')}</AlertDialogTitle>
                                        <AlertDialogDescription className="text-zinc-400">
                                          {userItem.name}{t('admin.deleteConfirm')}
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel className="border-white/20 text-zinc-400">{t('common.cancel')}</AlertDialogCancel>
                                        <AlertDialogAction
                                          className="bg-red-500 text-white hover:bg-red-600"
                                          onClick={() => handleDeleteUser(userItem.id)}
                                        >
                                          {t('common.delete')}
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && (
                    <p className="text-zinc-500 text-center py-8">{t('admin.noUsers')}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Funds Tab */}
          <TabsContent value="funds">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">{t('admin.funds')} ({funds.length})</h3>
              <Button
                onClick={handleNewFund}
                className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                data-testid="new-fund-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('admin.newFund')}
              </Button>
            </div>
            <div className="grid gap-4">
              {funds.map((fund) => (
                <Card key={fund.id} className="card-presage rounded-sm" data-testid={`fund-card-${fund.id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {fund.image_url && (
                          <img src={fund.image_url} alt={fund.name} className="w-16 h-16 object-cover rounded-sm" />
                        )}
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white font-semibold">{fund.name}</h3>
                            <Badge className={`${fund.status === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-zinc-500/10 text-zinc-500'} border-0`}>
                              {fund.status === 'active' ? t('admin.fundActive') : fund.status}
                            </Badge>
                            {fund.is_guaranteed && (
                              <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-0">
                                {fund.guarantee_type === 'full' ? t('admin.guaranteeFull') : fund.guarantee_type === 'partial' ? t('admin.guaranteePartial') : t('admin.guaranteeExists')}
                                {fund.guarantee_rate ? ` ${fund.guarantee_rate}%` : ''}
                              </Badge>
                            )}
                          </div>
                          <p className="text-zinc-400 text-sm">
                            {t('funds.targetYield')}: {fund.target_yield}% | {t('funds.minInvestment')}: {formatCurrency(fund.min_investment)} | {t('funds.totalRaised')}: {formatCurrency(fund.total_raised)}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-white/20 text-zinc-400 hover:text-white rounded-sm"
                          onClick={() => handleEditFund(fund)}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          {t('common.edit')}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-sm"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#141414] border-white/10">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">{t('admin.fundDeleteConfirm')}</AlertDialogTitle>
                              <AlertDialogDescription className="text-zinc-400">
                                {fund.name} {t('admin.deleteConfirm')}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="border-white/20 text-zinc-400">{t('common.cancel')}</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-red-500 text-white hover:bg-red-600"
                                onClick={() => handleDeleteFund(fund.id)}
                              >
                                {t('common.delete')}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages">
            {/* Company Info (About) - Structured Form */}
            <Card className="card-presage rounded-sm overflow-hidden mb-6" data-testid="company-info-card">
              <div
                className="p-5 flex items-center justify-between cursor-pointer hover:bg-white/[0.03] transition-colors"
                onClick={() => setExpandedPage(expandedPage === 'company' ? null : 'company')}
                data-testid="page-toggle-about"
              >
                <div className="flex items-center gap-3">
                  <div className="text-zinc-400"><Building2 className="h-5 w-5" /></div>
                  <div>
                    <h3 className="text-white font-semibold">{t('admin.about')}</h3>
                    <p className="text-zinc-500 text-xs mt-0.5">/about</p>
                  </div>
                </div>
                {expandedPage === 'company' ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-500" />}
              </div>

              {expandedPage === 'company' && (
                <div className="border-t border-white/10 p-5 space-y-5">
                  {/* Logo inside Company Info */}
                  <div className="flex items-center gap-6 pb-4 border-b border-white/10">
                    <div className="w-20 h-20 bg-[#0A0A0A] border border-white/15 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
                      {logoInfo?.has_logo ? (
                        <img src={`${API_URL}/api/site/logo?t=${Date.now()}`} alt="Logo" className="w-full h-full object-contain p-2" data-testid="logo-preview" />
                      ) : (
                        <Image className="h-7 w-7 text-zinc-700" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300 font-semibold">{t('admin.logoUpload')}</Label>
                      <div className="flex gap-2">
                        <input ref={logoInputRef} type="file" accept=".jpg,.jpeg,.png,.svg,.webp" onChange={handleLogoUpload} className="hidden" data-testid="logo-file-input" />
                        <Button size="sm" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="logo-upload-button">
                          {uploadingLogo ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                          {logoInfo?.has_logo ? t('admin.logoChange') : t('admin.logoUpload')}
                        </Button>
                        {logoInfo?.has_logo && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm" data-testid="logo-delete-button">
                                <Trash2 className="h-4 w-4 mr-1" />{t('common.delete')}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#141414] border-white/10">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">{t('admin.logoDeleted')}</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">{t('admin.fundDeleteConfirm')}</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-white/20 text-zinc-400">{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-500 text-white hover:bg-red-600" onClick={handleDeleteLogo}>{t('common.delete')}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                      <p className="text-zinc-600 text-xs">JPG, PNG, SVG, WEBP (最大5MB)</p>
                    </div>
                  </div>

                  <Tabs defaultValue="ja">
                    <TabsList className="bg-[#0A0A0A] mb-4">
                      <TabsTrigger value="ja" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-sm">
                        <FlagImg code="ja" className="w-4 h-auto inline mr-1 rounded-sm" /> 日本語
                      </TabsTrigger>
                      <TabsTrigger value="en" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-sm">
                        <FlagImg code="en" className="w-4 h-auto inline mr-1 rounded-sm" /> English
                      </TabsTrigger>
                      <TabsTrigger value="pt" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-sm">
                        <FlagImg code="pt" className="w-4 h-auto inline mr-1 rounded-sm" /> Português
                      </TabsTrigger>
                    </TabsList>

                    {/* Japanese */}
                    <TabsContent value="ja" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-sm">会社名</Label>
                          <Input value={companyInfo.company_name} onChange={(e) => setCompanyInfo({ ...companyInfo, company_name: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" data-testid="company-name-ja" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">代表者</Label>
                          <Input value={companyInfo.representative} onChange={(e) => setCompanyInfo({ ...companyInfo, representative: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" data-testid="company-rep-ja" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-sm">設立日</Label>
                          <Input value={companyInfo.established} onChange={(e) => setCompanyInfo({ ...companyInfo, established: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="2020年1月" data-testid="company-established" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">資本金</Label>
                          <Input value={companyInfo.capital} onChange={(e) => setCompanyInfo({ ...companyInfo, capital: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="1,000万円" data-testid="company-capital-ja" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">所在地</Label>
                        <Input value={companyInfo.address} onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" data-testid="company-address-ja" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">事業内容</Label>
                        <Textarea value={companyInfo.business} onChange={(e) => setCompanyInfo({ ...companyInfo, business: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[60px]" data-testid="company-business-ja" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">その他</Label>
                        <Textarea value={companyInfo.notes} onChange={(e) => setCompanyInfo({ ...companyInfo, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[60px]" placeholder="自由記述" data-testid="company-notes-ja" />
                      </div>
                    </TabsContent>

                    {/* English */}
                    <TabsContent value="en" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-sm">Company Name</Label>
                          <Input value={companyInfo.company_name_en} onChange={(e) => setCompanyInfo({ ...companyInfo, company_name_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">Representative</Label>
                          <Input value={companyInfo.representative_en} onChange={(e) => setCompanyInfo({ ...companyInfo, representative_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-sm">Established</Label>
                          <Input value={companyInfo.established} disabled className="bg-[#0A0A0A] border-white/10 text-zinc-500 rounded-sm mt-1" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">Capital</Label>
                          <Input value={companyInfo.capital_en} onChange={(e) => setCompanyInfo({ ...companyInfo, capital_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Address</Label>
                        <Input value={companyInfo.address_en} onChange={(e) => setCompanyInfo({ ...companyInfo, address_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Business</Label>
                        <Textarea value={companyInfo.business_en} onChange={(e) => setCompanyInfo({ ...companyInfo, business_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[60px]" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Notes</Label>
                        <Textarea value={companyInfo.notes_en} onChange={(e) => setCompanyInfo({ ...companyInfo, notes_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[60px]" />
                      </div>
                    </TabsContent>

                    {/* Portuguese */}
                    <TabsContent value="pt" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-sm">Nome da Empresa</Label>
                          <Input value={companyInfo.company_name_pt} onChange={(e) => setCompanyInfo({ ...companyInfo, company_name_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">Representante</Label>
                          <Input value={companyInfo.representative_pt} onChange={(e) => setCompanyInfo({ ...companyInfo, representative_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400 text-sm">Fundação</Label>
                          <Input value={companyInfo.established} disabled className="bg-[#0A0A0A] border-white/10 text-zinc-500 rounded-sm mt-1" />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-sm">Capital</Label>
                          <Input value={companyInfo.capital_pt} onChange={(e) => setCompanyInfo({ ...companyInfo, capital_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Endereço</Label>
                        <Input value={companyInfo.address_pt} onChange={(e) => setCompanyInfo({ ...companyInfo, address_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Negócio</Label>
                        <Textarea value={companyInfo.business_pt} onChange={(e) => setCompanyInfo({ ...companyInfo, business_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[60px]" />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Outros</Label>
                        <Textarea value={companyInfo.notes_pt} onChange={(e) => setCompanyInfo({ ...companyInfo, notes_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[60px]" />
                      </div>
                    </TabsContent>
                  </Tabs>

                  {/* Shared fields (language-independent) */}
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/10">
                    <div>
                      <Label className="text-zinc-400 text-sm">電話番号</Label>
                      <Input value={companyInfo.phone} onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="+81-XX-XXXX-XXXX" data-testid="company-phone" />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-sm">メールアドレス</Label>
                      <Input value={companyInfo.email} onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="info@example.com" data-testid="company-email" />
                    </div>
                  </div>

                  {/* Directors Section */}
                  <div className="pt-3 border-t border-white/10">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-zinc-300 font-semibold">取締役一覧</Label>
                      <Button size="sm" onClick={addDirector} className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-sm" data-testid="add-director-button">
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        {t('admin.about')} +
                      </Button>
                    </div>
                    {companyInfo.directors.length === 0 ? (
                      <p className="text-zinc-600 text-sm">取締役が登録されていません</p>
                    ) : (
                      <div className="space-y-3">
                        {companyInfo.directors.map((d, i) => (
                          <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4" data-testid={`director-row-${i}`}>
                            <div className="flex items-start justify-between mb-3">
                              <span className="text-zinc-400 text-xs font-semibold">#{i + 1}</span>
                              <Button size="sm" variant="ghost" onClick={() => removeDirector(i)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 w-6 p-0" data-testid={`remove-director-${i}`}>
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                            <div className="flex gap-4">
                              {/* Photo */}
                              <div className="flex-shrink-0">
                                <div className="w-20 h-20 bg-[#141414] border border-white/10 rounded-full overflow-hidden flex items-center justify-center">
                                  {d.photo_id ? (
                                    <img src={`${API_URL}/api/director-photo/${d.photo_id}`} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <User className="h-8 w-8 text-zinc-700" />
                                  )}
                                </div>
                                <div className="flex justify-center gap-1 mt-2">
                                  <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => handleDirectorPhotoUpload(i, e)} className="hidden" id={`director-photo-${i}`} data-testid={`director-photo-input-${i}`} />
                                  <Button size="sm" variant="ghost" onClick={() => document.getElementById(`director-photo-${i}`)?.click()} className="text-zinc-500 hover:text-white h-6 px-1.5 text-xs" data-testid={`director-photo-upload-${i}`}>
                                    <Upload className="h-3 w-3" />
                                  </Button>
                                  {d.photo_id && (
                                    <Button size="sm" variant="ghost" onClick={() => handleDeleteDirectorPhoto(i)} className="text-red-500 hover:text-red-400 h-6 px-1.5 text-xs" data-testid={`director-photo-delete-${i}`}>
                                      <X className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              {/* Fields */}
                              <div className="flex-1 grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-zinc-500 text-xs">氏名（日）</Label>
                                  <Input value={d.name || ''} onChange={(e) => updateDirector(i, 'name', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-zinc-500 text-xs">役職（日）</Label>
                                  <Input value={d.title || ''} onChange={(e) => updateDirector(i, 'title', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" placeholder="取締役" />
                                </div>
                                <div>
                                  <Label className="text-zinc-500 text-xs">Name (EN)</Label>
                                  <Input value={d.name_en || ''} onChange={(e) => updateDirector(i, 'name_en', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-zinc-500 text-xs">Title (EN)</Label>
                                  <Input value={d.title_en || ''} onChange={(e) => updateDirector(i, 'title_en', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" placeholder="Director" />
                                </div>
                                <div>
                                  <Label className="text-zinc-500 text-xs">Nome (PT)</Label>
                                  <Input value={d.name_pt || ''} onChange={(e) => updateDirector(i, 'name_pt', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-zinc-500 text-xs">Cargo (PT)</Label>
                                  <Input value={d.title_pt || ''} onChange={(e) => updateDirector(i, 'title_pt', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" placeholder="Diretor" />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Save */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                    <Button variant="outline" onClick={() => setExpandedPage(null)} className="border-white/20 text-zinc-400 hover:text-white rounded-sm" size="sm">
                      {t('common.cancel')}
                    </Button>
                    <Button onClick={handleSaveCompanyInfo} disabled={savingCompany} className="bg-white text-black hover:bg-zinc-200 rounded-sm" size="sm" data-testid="save-company-info">
                      {savingCompany ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />保存中...</> : <><Save className="h-4 w-4 mr-1" />保存</>}
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Structured Page Sections (terms, privacy, risk) */}
            <div className="space-y-3">
              {Object.entries(structuredPageConfig).map(([key, cfg]) => (
                <Card key={key} className="card-presage rounded-sm overflow-hidden" data-testid={`page-card-${key}`}>
                  <div
                    className="p-5 flex items-center justify-between cursor-pointer hover:bg-white/[0.03] transition-colors"
                    onClick={() => setExpandedPage(expandedPage === key ? null : key)}
                    data-testid={`page-toggle-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-zinc-400">{cfg.icon}</div>
                      <div>
                        <h3 className="text-white font-semibold">{cfg.title}</h3>
                        <p className="text-zinc-500 text-xs mt-0.5">/{key} — {structuredPages[key]?.length || 0} セクション</p>
                      </div>
                    </div>
                    {expandedPage === key ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-500" />}
                  </div>

                  {expandedPage === key && (
                    <div className="border-t border-white/10 p-5 space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-zinc-400 text-sm">セクションごとに見出しと内容を入力できます</p>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => addSection(key)} className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-sm" data-testid={`add-section-${key}`}>
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            {t('common.edit')} +
                          </Button>
                        </div>
                      </div>

                      {structuredPages[key].length === 0 ? (
                        <div className="text-center py-6 border border-dashed border-white/10 rounded-sm">
                          <p className="text-zinc-600 text-sm">セクションがありません。「セクション追加」をクリックして始めてください。</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {structuredPages[key].map((sec, i) => (
                            <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4" data-testid={`section-${key}-${i}`}>
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-zinc-400 text-xs font-semibold">セクション #{i + 1}</span>
                                <Button size="sm" variant="ghost" onClick={() => removeSection(key, i)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 w-6 p-0" data-testid={`remove-section-${key}-${i}`}>
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </div>

                              <Tabs defaultValue="ja">
                                <TabsList className="bg-[#141414] mb-3 h-8">
                                  <TabsTrigger value="ja" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-xs h-7"><FlagImg code="ja" /></TabsTrigger>
                                  <TabsTrigger value="en" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-xs h-7"><FlagImg code="en" /></TabsTrigger>
                                  <TabsTrigger value="pt" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-xs h-7"><FlagImg code="pt" /></TabsTrigger>
                                </TabsList>

                                <TabsContent value="ja" className="space-y-2">
                                  <Input value={sec.heading || ''} onChange={(e) => updateSection(key, i, 'heading', e.target.value)} placeholder="見出し" className="bg-[#141414] border-white/10 text-white rounded-sm h-8 text-sm" />
                                  <Textarea value={sec.content || ''} onChange={(e) => updateSection(key, i, 'content', e.target.value)} placeholder="内容" className="bg-[#141414] border-white/10 text-white rounded-sm min-h-[80px] text-sm" />
                                </TabsContent>
                                <TabsContent value="en" className="space-y-2">
                                  <Input value={sec.heading_en || ''} onChange={(e) => updateSection(key, i, 'heading_en', e.target.value)} placeholder="Heading" className="bg-[#141414] border-white/10 text-white rounded-sm h-8 text-sm" />
                                  <Textarea value={sec.content_en || ''} onChange={(e) => updateSection(key, i, 'content_en', e.target.value)} placeholder="Content" className="bg-[#141414] border-white/10 text-white rounded-sm min-h-[80px] text-sm" />
                                </TabsContent>
                                <TabsContent value="pt" className="space-y-2">
                                  <Input value={sec.heading_pt || ''} onChange={(e) => updateSection(key, i, 'heading_pt', e.target.value)} placeholder="Título" className="bg-[#141414] border-white/10 text-white rounded-sm h-8 text-sm" />
                                  <Textarea value={sec.content_pt || ''} onChange={(e) => updateSection(key, i, 'content_pt', e.target.value)} placeholder="Conteúdo" className="bg-[#141414] border-white/10 text-white rounded-sm min-h-[80px] text-sm" />
                                </TabsContent>
                              </Tabs>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                        <Button variant="outline" onClick={() => setExpandedPage(null)} className="border-white/20 text-zinc-400 hover:text-white rounded-sm" size="sm">
                          {t('common.cancel')}
                        </Button>
                        <Button
                          onClick={() => handleSaveStructuredPage(key)}
                          disabled={savingStructured === key}
                          className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                          size="sm"
                          data-testid={`save-page-${key}`}
                        >
                          {savingStructured === key ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />保存中...</> : <><Save className="h-4 w-4 mr-1" />保存</>}
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>

            {/* Custom Pages */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">カスタムページ</h3>
                <Button size="sm" onClick={() => setNewPageDialogOpen(true)} className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="add-custom-page">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('admin.newPageCreate')}
                </Button>
              </div>

              {customPages.length === 0 ? (
                <div className="text-center py-6 border border-dashed border-white/10 rounded-sm">
                  <FileText className="h-8 w-8 text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-600 text-sm">カスタムページはありません</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {customPages.map((cp) => (
                    <Card key={cp.slug} className="card-presage rounded-sm overflow-hidden" data-testid={`page-card-${cp.slug}`}>
                      <div
                        className="p-5 flex items-center justify-between cursor-pointer hover:bg-white/[0.03] transition-colors"
                        onClick={() => setExpandedPage(expandedPage === cp.slug ? null : cp.slug)}
                        data-testid={`page-toggle-${cp.slug}`}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-zinc-400" />
                          <div>
                            <h3 className="text-white font-semibold">{cp.title_ja}</h3>
                            <p className="text-zinc-500 text-xs mt-0.5">/{cp.slug} — {customPageSections[cp.slug]?.length || 0} セクション</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0" onClick={(e) => e.stopPropagation()} data-testid={`delete-page-${cp.slug}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#141414] border-white/10">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">ページ削除</AlertDialogTitle>
                                <AlertDialogDescription className="text-zinc-400">「{cp.title_ja}」を削除しますか？この操作は取り消せません。</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="border-white/20 text-zinc-400">{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-500 text-white hover:bg-red-600" onClick={() => handleDeleteCustomPage(cp.slug)}>{t('common.delete')}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          {expandedPage === cp.slug ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-500" />}
                        </div>
                      </div>

                      {expandedPage === cp.slug && (
                        <div className="border-t border-white/10 p-5 space-y-4">
                          <div className="flex items-center justify-between">
                            <p className="text-zinc-400 text-sm">セクションごとに見出しと内容を入力</p>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => addCustomSection(cp.slug)} className="bg-zinc-800 text-white hover:bg-zinc-700 rounded-sm">
                                <Plus className="h-3.5 w-3.5 mr-1" />セクション追加
                              </Button>
                            </div>
                          </div>

                          {(customPageSections[cp.slug] || []).length === 0 ? (
                            <div className="text-center py-6 border border-dashed border-white/10 rounded-sm">
                              <p className="text-zinc-600 text-sm">セクションがありません</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {(customPageSections[cp.slug] || []).map((sec, i) => (
                                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-zinc-400 text-xs font-semibold">セクション #{i + 1}</span>
                                    <Button size="sm" variant="ghost" onClick={() => removeCustomSection(cp.slug, i)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 w-6 p-0">
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                  <Tabs defaultValue="ja">
                                    <TabsList className="bg-[#141414] mb-3 h-8">
                                      <TabsTrigger value="ja" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-xs h-7"><FlagImg code="ja" /></TabsTrigger>
                                      <TabsTrigger value="en" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-xs h-7"><FlagImg code="en" /></TabsTrigger>
                                      <TabsTrigger value="pt" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300 text-xs h-7"><FlagImg code="pt" /></TabsTrigger>
                                    </TabsList>
                                    <TabsContent value="ja" className="space-y-2">
                                      <Input value={sec.heading || ''} onChange={(e) => updateCustomSection(cp.slug, i, 'heading', e.target.value)} placeholder="見出し" className="bg-[#141414] border-white/10 text-white rounded-sm h-8 text-sm" />
                                      <Textarea value={sec.content || ''} onChange={(e) => updateCustomSection(cp.slug, i, 'content', e.target.value)} placeholder="内容" className="bg-[#141414] border-white/10 text-white rounded-sm min-h-[80px] text-sm" />
                                    </TabsContent>
                                    <TabsContent value="en" className="space-y-2">
                                      <Input value={sec.heading_en || ''} onChange={(e) => updateCustomSection(cp.slug, i, 'heading_en', e.target.value)} placeholder="Heading" className="bg-[#141414] border-white/10 text-white rounded-sm h-8 text-sm" />
                                      <Textarea value={sec.content_en || ''} onChange={(e) => updateCustomSection(cp.slug, i, 'content_en', e.target.value)} placeholder="Content" className="bg-[#141414] border-white/10 text-white rounded-sm min-h-[80px] text-sm" />
                                    </TabsContent>
                                    <TabsContent value="pt" className="space-y-2">
                                      <Input value={sec.heading_pt || ''} onChange={(e) => updateCustomSection(cp.slug, i, 'heading_pt', e.target.value)} placeholder="Título" className="bg-[#141414] border-white/10 text-white rounded-sm h-8 text-sm" />
                                      <Textarea value={sec.content_pt || ''} onChange={(e) => updateCustomSection(cp.slug, i, 'content_pt', e.target.value)} placeholder="Conteúdo" className="bg-[#141414] border-white/10 text-white rounded-sm min-h-[80px] text-sm" />
                                    </TabsContent>
                                  </Tabs>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                            <Button variant="outline" onClick={() => setExpandedPage(null)} className="border-white/20 text-zinc-400 hover:text-white rounded-sm" size="sm">{t('common.cancel')}</Button>
                            <Button onClick={() => handleSaveCustomPage(cp.slug)} disabled={savingStructured === cp.slug} className="bg-white text-black hover:bg-zinc-200 rounded-sm" size="sm" data-testid={`save-page-${cp.slug}`}>
                              {savingStructured === cp.slug ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />保存中...</> : <><Save className="h-4 w-4 mr-1" />保存</>}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            {contacts.length === 0 ? (
              <Card className="card-presage rounded-sm">
                <CardContent className="p-12 text-center">
                  <Mail className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">お問い合わせはありません</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {/* Filter summary */}
                <div className="flex items-center gap-4 mb-2">
                  <span className="text-zinc-400 text-sm">
                    {contacts.length} {t('admin.items')}
                  </span>
                  {contacts.filter(c => c.status === 'new').length > 0 && (
                    <Badge className="bg-blue-500/15 text-blue-400 border-blue-400/40 border">
                      {t('admin.statusNew')} {contacts.filter(c => c.status === 'new').length}
                    </Badge>
                  )}
                </div>

                {contacts.map((contact) => (
                  <Card key={contact.id} className="card-presage rounded-sm overflow-hidden" data-testid={`contact-card-${contact.id}`}>
                    {/* Header row - clickable */}
                    <div
                      className="p-5 flex items-center justify-between cursor-pointer hover:bg-white/[0.03] transition-colors"
                      onClick={() => toggleContact(contact)}
                      data-testid={`contact-toggle-${contact.id}`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          contact.status === 'new' ? 'bg-blue-400' :
                          contact.status === 'read' ? 'bg-yellow-400' : 'bg-emerald-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-white font-semibold truncate">{contact.subject}</h3>
                            {getStatusBadge(contact.status)}
                          </div>
                          <p className="text-zinc-400 text-sm truncate">
                            {contact.name} &lt;{contact.email}&gt; — {new Date(contact.created_at).toLocaleString('ja-JP')}
                          </p>
                        </div>
                      </div>
                      {expandedContact === contact.id ? (
                        <ChevronUp className="h-5 w-5 text-zinc-400 flex-shrink-0 ml-3" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-zinc-500 flex-shrink-0 ml-3" />
                      )}
                    </div>

                    {/* Expanded detail */}
                    {expandedContact === contact.id && (
                      <div className="border-t border-white/10 p-5 space-y-4">
                        {/* Contact info */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-zinc-500">名前:</span>
                            <span className="text-white ml-2">{contact.name}</span>
                          </div>
                          <div>
                            <span className="text-zinc-500">メール:</span>
                            <a href={`mailto:${contact.email}`} className="text-blue-400 hover:text-blue-300 ml-2">{contact.email}</a>
                          </div>
                          <div>
                            <span className="text-zinc-500">受信日:</span>
                            <span className="text-zinc-300 ml-2">{new Date(contact.created_at).toLocaleString('ja-JP')}</span>
                          </div>
                          {contact.replied_at && (
                            <div>
                              <span className="text-zinc-500">返信日:</span>
                              <span className="text-zinc-300 ml-2">{new Date(contact.replied_at).toLocaleString('ja-JP')}</span>
                            </div>
                          )}
                        </div>

                        {/* Message */}
                        <div>
                          <Label className="text-zinc-400 text-sm mb-2 block">メッセージ内容</Label>
                          <div className="bg-[#0A0A0A] p-4 rounded-sm border border-white/10">
                            <p className="text-zinc-200 whitespace-pre-wrap text-sm leading-relaxed">{contact.message}</p>
                          </div>
                        </div>

                        {/* Admin Notes */}
                        <div>
                          <Label className="text-zinc-400 text-sm mb-2 block">管理者メモ</Label>
                          <Textarea
                            value={contactNotes[contact.id] ?? contact.admin_notes ?? ''}
                            onChange={(e) => setContactNotes({ ...contactNotes, [contact.id]: e.target.value })}
                            placeholder={t('admin.memoLabel')}
                            className="bg-[#0A0A0A] border-white/10 text-white rounded-sm min-h-[80px] text-sm"
                            data-testid={`contact-notes-${contact.id}`}
                          />
                          <div className="flex justify-end mt-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveContactNotes(contact.id)}
                              className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                              data-testid={`save-notes-${contact.id}`}
                            >
                              <Save className="h-3.5 w-3.5 mr-1" />
                              {t('admin.saved')}
                            </Button>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-3 border-t border-white/5">
                          <div className="flex gap-2">
                            <Label className="text-zinc-500 text-sm mr-2 self-center">ステータス:</Label>
                            <Select
                              value={contact.status}
                              onValueChange={(value) => handleUpdateContactStatus(contact.id, value)}
                            >
                              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm w-[140px] h-9" data-testid={`contact-status-select-${contact.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#1C1C1C] border-white/10">
                                <SelectItem value="new" className="text-zinc-300">新規</SelectItem>
                                <SelectItem value="read" className="text-zinc-300">確認済</SelectItem>
                                <SelectItem value="replied" className="text-zinc-300">返信済</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`mailto:${contact.email}?subject=Re: ${encodeURIComponent(contact.subject)}`, '_blank')}
                              className="border-white/20 text-zinc-300 hover:text-white rounded-sm"
                              data-testid={`contact-reply-email-${contact.id}`}
                            >
                              <Mail className="h-3.5 w-3.5 mr-1" />
                              {t('admin.contactReplied')}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm"
                                  data-testid={`contact-delete-${contact.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                                  {t('common.delete')}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-[#141414] border-white/10">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">{t('common.delete')}</AlertDialogTitle>
                                  <AlertDialogDescription className="text-zinc-400">
                                    {contact.subject} {t('admin.deleteConfirm')}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="border-white/20 text-zinc-400">{t('common.cancel')}</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-red-500 text-white hover:bg-red-600"
                                    onClick={() => handleDeleteContact(contact.id)}
                                  >
                                    {t('common.delete')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Menu Tab */}
          <TabsContent value="menu">
            <Card className="card-presage rounded-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-white font-semibold">{t('admin.menuCat')}</h3>
                    <p className="text-zinc-500 text-sm mt-1">{t('admin.menuCat')}</p>
                  </div>
                  <Button size="sm" onClick={addMenuItem} className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="add-menu-item">
                    <Plus className="h-4 w-4 mr-1" />
                    {t('common.edit')} +
                  </Button>
                </div>

                {menuItems.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-white/10 rounded-sm">
                    <MenuIcon className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 text-sm">{t('admin.noWithdrawals')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {menuItems.map((item, i) => (
                      <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4" data-testid={`menu-item-${i}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-500 text-xs font-semibold">#{i + 1}</span>
                            {item.visible ? (
                              <span className="text-emerald-400 text-xs flex items-center gap-1"><Eye className="h-3 w-3" />{t('admin.fundActive')}</span>
                            ) : (
                              <span className="text-zinc-600 text-xs flex items-center gap-1"><EyeOff className="h-3 w-3" />{t('admin.statusPrivate')}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => moveMenuItem(i, -1)} disabled={i === 0} className="text-zinc-500 hover:text-white h-7 w-7 p-0" data-testid={`menu-move-up-${i}`}>
                              <ArrowUp className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => moveMenuItem(i, 1)} disabled={i === menuItems.length - 1} className="text-zinc-500 hover:text-white h-7 w-7 p-0" data-testid={`menu-move-down-${i}`}>
                              <ArrowDown className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => updateMenuItem(i, 'visible', !item.visible)} className={`h-7 w-7 p-0 ${item.visible ? 'text-emerald-400 hover:text-emerald-300' : 'text-zinc-600 hover:text-zinc-400'}`} data-testid={`menu-toggle-visible-${i}`}>
                              {item.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => removeMenuItem(i)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0" data-testid={`menu-remove-${i}`}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          <div>
                            <Label className="text-zinc-500 text-xs">JA</Label>
                            <Input value={item.label || ''} onChange={(e) => updateMenuItem(i, 'label', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" data-testid={`menu-label-ja-${i}`} />
                          </div>
                          <div>
                            <Label className="text-zinc-500 text-xs">Name (EN)</Label>
                            <Input value={item.label_en || ''} onChange={(e) => updateMenuItem(i, 'label_en', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" />
                          </div>
                          <div>
                            <Label className="text-zinc-500 text-xs">Nome (PT)</Label>
                            <Input value={item.label_pt || ''} onChange={(e) => updateMenuItem(i, 'label_pt', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm" />
                          </div>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label className="text-zinc-500 text-xs">URL</Label>
                              <Input value={item.url || ''} onChange={(e) => updateMenuItem(i, 'url', e.target.value)} className="bg-[#141414] border-white/10 text-white rounded-sm mt-0.5 h-8 text-sm font-mono" placeholder="/" data-testid={`menu-url-${i}`} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end mt-6 pt-4 border-t border-white/5">
                  <Button onClick={handleSaveMenu} disabled={savingMenu} className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="save-menu">
                    {savingMenu ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('admin.saving')}</> : <><Save className="h-4 w-4 mr-2" />{t('common.save')}</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals">
            {withdrawals.length === 0 ? (
              <Card className="card-presage rounded-sm">
                <CardContent className="p-12 text-center">
                  <Wallet className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">{t('admin.noWithdrawals')}</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-presage rounded-sm">
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/15">
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.roleUser')}</th>
                          <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.amount')}</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.method')}</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.status')}</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.date')}</th>
                          <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawals.map((w) => (
                          <tr key={w.id} className="border-b border-white/5 hover:bg-white/[0.07] transition-colors" data-testid={`withdrawal-row-${w.id}`}>
                            <td className="py-4 px-2">
                              <p className="text-white font-medium">{w.user_name}</p>
                              <p className="text-zinc-400 text-xs">{w.user_email}</p>
                            </td>
                            <td className="py-4 px-2 text-right">
                              <p className="text-white font-mono">{formatCurrency(w.amount)}</p>
                              {w.exchange_info && (
                                <p className="text-zinc-400 text-xs">
                                  → {w.exchange_info.estimated_amount} {w.exchange_info.target_currency}
                                </p>
                              )}
                            </td>
                            <td className="py-4 px-2 text-zinc-300">{w.method}</td>
                            <td className="py-4 px-2">{getWithdrawalStatusBadge(w.status)}</td>
                            <td className="py-4 px-2 text-zinc-400 text-sm">
                              {new Date(w.created_at).toLocaleDateString('ja-JP')}
                            </td>
                            <td className="py-4 px-2 text-right">
                              <div className="flex justify-end gap-2">
                                {w.status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 rounded-sm"
                                      onClick={() => handleUpdateWithdrawalStatus(w.id, 'approved')}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-sm"
                                      onClick={() => handleUpdateWithdrawalStatus(w.id, 'rejected')}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                {w.status === 'approved' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-blue-500/30 text-blue-500 hover:bg-blue-500/10 rounded-sm"
                                    onClick={() => handleUpdateWithdrawalStatus(w.id, 'completed')}
                                  >
                                    {t('admin.statusCompleted')}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Deposits Tab */}
          <TabsContent value="deposits">
            {deposits.length === 0 ? (
              <Card className="card-presage rounded-sm">
                <CardContent className="p-12 text-center">
                  <CreditCard className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">入金依頼はありません</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-presage rounded-sm">
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/15">
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.roleUser')}</th>
                          <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">金額</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">方法</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">振込名義</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">ステータス</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">申請日</th>
                          <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deposits.map((d) => (
                          <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.07] transition-colors" data-testid={`deposit-row-${d.id}`}>
                            <td className="py-4 px-2">
                              <p className="text-white font-medium">{d.user_name}</p>
                              <p className="text-zinc-400 text-xs">{d.user_email}</p>
                            </td>
                            <td className="py-4 px-2 text-right">
                              <p className="text-white font-mono">{formatCurrency(d.amount)}</p>
                            </td>
                            <td className="py-4 px-2 text-zinc-300 text-sm">
                              {d.method === 'prc_remit' ? 'PRC Remit' : 'Wise'}
                            </td>
                            <td className="py-4 px-2 text-zinc-300 text-sm">{d.depositor_name}</td>
                            <td className="py-4 px-2">{getDepositStatusBadge(d.status)}</td>
                            <td className="py-4 px-2 text-zinc-400 text-sm">
                              {new Date(d.created_at).toLocaleDateString('ja-JP')}
                            </td>
                            <td className="py-4 px-2 text-right">
                              <div className="flex justify-end gap-2">
                                {d.status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10 rounded-sm"
                                      onClick={() => handleUpdateDepositStatus(d.id, 'approved')}
                                      data-testid={`deposit-approve-${d.id}`}
                                    >
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-sm"
                                      onClick={() => handleUpdateDepositStatus(d.id, 'rejected')}
                                      data-testid={`deposit-reject-${d.id}`}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Deposit Methods Settings */}
            <Card className="card-presage rounded-sm mt-6">
              <CardHeader>
                <CardTitle className="text-white text-lg">入金方法の設定</CardTitle>
                <p className="text-zinc-500 text-sm">ユーザーに表示する送金先情報を設定します</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Wise Settings */}
                <div className="p-4 bg-[#0A0A0A] rounded-sm border border-white/10">
                  <h4 className="text-white font-semibold mb-4">Wise</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">{t('admin.accountName')}</Label>
                      <Input
                        value={depositMethods.wise.account_name || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, wise: { ...prev.wise, account_name: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm"
                        placeholder="e.g. Presage Ltd."
                        data-testid="wise-account-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">{t('admin.email')}</Label>
                      <Input
                        value={depositMethods.wise.account_email || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, wise: { ...prev.wise, account_email: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm"
                        placeholder="e.g. payments@presage.ltd"
                        data-testid="wise-account-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">{t('admin.currency')}</Label>
                      <Input
                        value={depositMethods.wise.currency || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, wise: { ...prev.wise, currency: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm"
                        placeholder="e.g. JPY"
                        data-testid="wise-currency"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">{t('admin.feeRate')}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={depositMethods.wise.fee_rate || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, wise: { ...prev.wise, fee_rate: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm"
                        placeholder="e.g. 0.5"
                        data-testid="wise-fee-rate"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-zinc-400">{t('admin.notes')}</Label>
                      <Textarea
                        value={depositMethods.wise.notes || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, wise: { ...prev.wise, notes: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm min-h-[60px]"
                        placeholder={t('admin.optional')}
                        data-testid="wise-notes"
                      />
                    </div>
                  </div>
                </div>

                {/* PRC Remit Settings */}
                <div className="p-4 bg-[#0A0A0A] rounded-sm border border-white/10">
                  <h4 className="text-white font-semibold mb-4">PRC Remit</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-400">{t('admin.accountName')}</Label>
                      <Input
                        value={depositMethods.prc_remit.account_name || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, prc_remit: { ...prev.prc_remit, account_name: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm"
                        placeholder="e.g. Presage Ltd."
                        data-testid="prc-account-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">{t('admin.accountId')}</Label>
                      <Input
                        value={depositMethods.prc_remit.account_id || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, prc_remit: { ...prev.prc_remit, account_id: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm"
                        placeholder="e.g. PRC-12345678"
                        data-testid="prc-account-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">{t('admin.currency')}</Label>
                      <Input
                        value={depositMethods.prc_remit.currency || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, prc_remit: { ...prev.prc_remit, currency: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm"
                        placeholder="e.g. JPY"
                        data-testid="prc-currency"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-400">{t('admin.feeRate')}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={depositMethods.prc_remit.fee_rate || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, prc_remit: { ...prev.prc_remit, fee_rate: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm"
                        placeholder="e.g. 0.8"
                        data-testid="prc-fee-rate"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label className="text-zinc-400">{t('admin.notes')}</Label>
                      <Textarea
                        value={depositMethods.prc_remit.notes || ''}
                        onChange={(e) => setDepositMethods(prev => ({
                          ...prev, prc_remit: { ...prev.prc_remit, notes: e.target.value }
                        }))}
                        className="bg-[#1C1C1C] border-white/10 text-white rounded-sm min-h-[60px]"
                        placeholder={t('admin.optional')}
                        data-testid="prc-notes"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSaveDepositMethods}
                  disabled={savingDepositMethods}
                  className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                  data-testid="save-deposit-methods-btn"
                >
                  {savingDepositMethods ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />保存中...</>
                  ) : t('admin.saveDepositMethods')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Distributions Tab */}
          <TabsContent value="distributions">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">分配履歴 ({distributionBatches.length})</h3>
              <Button
                onClick={handleOpenDistributionDialog}
                className="bg-white text-black hover:bg-zinc-200 rounded-sm"
                data-testid="new-distribution-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('admin.newDistribution')}
              </Button>
            </div>
            {distributionBatches.length === 0 ? (
              <Card className="card-presage rounded-sm">
                <CardContent className="p-12 text-center">
                  <Coins className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500">分配履歴はありません</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="card-presage rounded-sm">
                <CardContent className="p-6">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/15">
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.fundCat')}</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">分配日</th>
                          <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">総収益</th>
                          <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">コスト</th>
                          <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">純利益</th>
                          <th className="text-right py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">分配総額</th>
                          <th className="text-center py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">投資家数</th>
                          <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">ステータス</th>
                        </tr>
                      </thead>
                      <tbody>
                        {distributionBatches.map((batch) => (
                          <tr key={batch.id} className="border-b border-white/5 hover:bg-white/[0.07] transition-colors" data-testid={`distribution-row-${batch.id}`}>
                            <td className="py-4 px-2 text-white font-medium">{batch.fund_name}</td>
                            <td className="py-4 px-2 text-zinc-300 text-sm">{batch.distribution_date}</td>
                            <td className="py-4 px-2 text-right text-white font-mono">{formatCurrency(batch.gross_revenue)}</td>
                            <td className="py-4 px-2 text-right text-zinc-300 font-mono">{formatCurrency(batch.costs)}</td>
                            <td className="py-4 px-2 text-right text-emerald-400 font-mono">{formatCurrency(batch.net_profit)}</td>
                            <td className="py-4 px-2 text-right text-white font-mono">{formatCurrency(batch.total_distributed)}</td>
                            <td className="py-4 px-2 text-center text-zinc-300">{batch.investor_count}</td>
                            <td className="py-4 px-2">
                              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-400/40 border">
                                {batch.status === 'executed' ? t('admin.distExecuted') : batch.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* KYC Tab */}
          <TabsContent value="kyc">
            <Card className="card-presage rounded-sm">
              <CardContent className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-white font-semibold">{t('admin.kyc')} ({kycSubmissions.length})</h3>
                </div>
                {kycSubmissions.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-500">{t('admin.noKycSubmissions')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {kycSubmissions.map((sub) => (
                      <div key={sub.id} className="border border-white/15 rounded-sm p-4 hover:bg-white/[0.03] transition-colors" data-testid={`kyc-row-${sub.id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <h4 className="text-white font-semibold">{sub.user_name}</h4>
                              {getKYCSubmissionBadge(sub.status)}
                            </div>
                            <p className="text-zinc-400 text-sm">{sub.user_email}</p>
                            <p className="text-zinc-500 text-xs mt-1">
                              {t('admin.docInfo', { type: getDocTypeName(sub.document_type), date: new Date(sub.submitted_at).toLocaleDateString() })}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={`${API_URL}/api/kyc/document-image/${sub.file_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm border border-blue-500/30 px-3 py-1.5 rounded-sm hover:bg-blue-500/10 transition-colors"
                              data-testid={`kyc-view-doc-${sub.id}`}
                            >
                              <FileText className="h-4 w-4" />
                              {t('admin.viewDoc')}
                            </a>
                          </div>
                        </div>
                        {sub.status === 'pending' && (
                          <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                            <Button
                              size="sm"
                              className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
                              onClick={() => handleReviewKYC(sub.id, 'approve')}
                              data-testid={`kyc-approve-${sub.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              {t('admin.approveBtn')}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-sm"
                                  data-testid={`kyc-reject-trigger-${sub.id}`}
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  {t('admin.rejectBtn')}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="bg-[#141414] border-white/10">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-white">{t('admin.kycRejectTitle')}</AlertDialogTitle>
                                  <AlertDialogDescription className="text-zinc-400">
                                    {t('admin.kycRejectConfirm', { name: sub.user_name })}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="border-white/20 text-zinc-400">{t('common.cancel')}</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-red-500 text-white hover:bg-red-600"
                                    onClick={() => handleReviewKYC(sub.id, 'reject', t('admin.kycDocError'))}
                                  >
                                    {t('admin.rejectBtn')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        )}
                        {sub.status === 'rejected' && sub.rejection_reason && (
                          <p className="text-red-400 text-xs mt-2">{t('admin.kycRejectedLabel')}: {sub.rejection_reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Logs Tab */}
          <TabsContent value="audit">
            <Card className="card-presage rounded-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <h3 className="text-white font-semibold">{t('admin.auditLogs')} ({auditTotal} {t('admin.items')})</h3>
                  <Select value={auditCategory} onValueChange={(v) => { setAuditCategory(v); setAuditPage(0); }}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm w-48" data-testid="audit-category-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1C] border-white/10">
                      <SelectItem value="all" className="text-zinc-300">{t('admin.all')}</SelectItem>
                      <SelectItem value="auth" className="text-zinc-300">{t('admin.authCat')}</SelectItem>
                      <SelectItem value="fund" className="text-zinc-300">{t('admin.fundCat')}</SelectItem>
                      <SelectItem value="user" className="text-zinc-300">{t('admin.roleUser')}</SelectItem>
                      <SelectItem value="kyc" className="text-zinc-300">KYC</SelectItem>
                      <SelectItem value="withdrawal" className="text-zinc-300">{t('admin.withdrawCat')}</SelectItem>
                      <SelectItem value="distribution" className="text-zinc-300">{t('admin.distCat')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {loadingAudit ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 bg-[#1C1C1C]" />)}
                  </div>
                ) : auditLogs.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">{t('admin.noLogs')}</p>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="audit-log-table">
                        <thead>
                          <tr className="border-b border-white/15">
                            <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.datetime')}</th>
                            <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.category')}</th>
                            <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.actions')}</th>
                            <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.executor')}</th>
                            <th className="text-left py-3 px-2 text-zinc-400 text-xs font-semibold uppercase tracking-wider">{t('admin.details')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {auditLogs.map((log) => {
                            const actionLabels = {
                              user_login: t('admin.auditActions.user_login'),
                              fund_created: t('admin.auditActions.fund_created'),
                              fund_updated: t('admin.auditActions.fund_updated'),
                              fund_deleted: t('admin.auditActions.fund_deleted'),
                              user_updated: t('admin.auditActions.user_updated'),
                              user_deleted: t('admin.auditActions.user_deleted'),
                              kyc_approved: t('admin.auditActions.kyc_approved'),
                              kyc_rejected: t('admin.auditActions.kyc_rejected'),
                              withdrawal_approved: t('admin.auditActions.withdrawal_approved'),
                              withdrawal_rejected: t('admin.auditActions.withdrawal_rejected'),
                              withdrawal_completed: t('admin.auditActions.withdrawal_completed'),
                              withdrawal_pending: t('admin.auditActions.withdrawal_pending'),
                              distribution_executed: t('admin.auditActions.distribution_executed')
                            };
                            const categoryColors = {
                              auth: 'bg-blue-500/10 text-blue-400',
                              fund: 'bg-emerald-500/10 text-emerald-400',
                              user: 'bg-purple-500/10 text-purple-400',
                              kyc: 'bg-amber-500/10 text-amber-400',
                              withdrawal: 'bg-orange-500/10 text-orange-400',
                              distribution: 'bg-cyan-500/10 text-cyan-400'
                            };
                            const detailStr = Object.entries(log.details || {}).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
                            return (
                              <tr key={log.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`audit-row-${log.id}`}>
                                <td className="py-3 px-2 text-zinc-400 text-xs whitespace-nowrap font-mono">
                                  {new Date(log.timestamp).toLocaleString('ja-JP')}
                                </td>
                                <td className="py-3 px-2">
                                  <Badge className={`${categoryColors[log.category] || 'bg-zinc-500/10 text-zinc-400'} border-0 text-xs`}>
                                    {log.category}
                                  </Badge>
                                </td>
                                <td className="py-3 px-2 text-white text-sm">
                                  {actionLabels[log.action] || log.action}
                                </td>
                                <td className="py-3 px-2 text-zinc-400 text-sm">{log.user_email}</td>
                                <td className="py-3 px-2 text-zinc-500 text-xs max-w-xs truncate">{detailStr || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {auditTotal > 30 && (
                      <div className="flex justify-center items-center gap-4 mt-4">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-white/20 text-zinc-400 rounded-sm"
                          disabled={auditPage === 0}
                          onClick={() => setAuditPage(p => Math.max(0, p - 1))}
                          data-testid="audit-prev-page"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-zinc-500 text-sm">
                          {auditPage + 1} / {Math.ceil(auditTotal / 30)}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-white/20 text-zinc-400 rounded-sm"
                          disabled={(auditPage + 1) * 30 >= auditTotal}
                          onClick={() => setAuditPage(p => p + 1)}
                          data-testid="audit-next-page"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp">
            <WhatsAppSettingsPanel />
          </TabsContent>
        </Tabs>
        <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">{t('admin.editUser')}</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-400">{t('admin.name')}</Label>
                  <Input value={editingUser.name || ''} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-zinc-400">{t('admin.email')}</Label>
                  <Input value={editingUser.email || ''} disabled className="bg-[#0A0A0A] border-white/10 text-zinc-500 rounded-sm mt-1" />
                </div>
                <div>
                  <Label className="text-zinc-400">{t('admin.role')}</Label>
                  <Select value={editingUser.role} onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1C] border-white/10">
                      <SelectItem value="user" className="text-zinc-300">{t('admin.roleUser')}</SelectItem>
                      <SelectItem value="admin" className="text-zinc-300">{t('admin.roleAdmin')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400">{t('admin.kycStatus')}</Label>
                  <Select value={editingUser.is_kyc_verified ? 'verified' : 'unverified'} onValueChange={(value) => setEditingUser({ ...editingUser, is_kyc_verified: value === 'verified', kyc_status: value === 'verified' ? 'verified' : 'not_started' })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1C1C1C] border-white/10">
                      <SelectItem value="unverified" className="text-zinc-300">{t('admin.unverifiedLabel')}</SelectItem>
                      <SelectItem value="verified" className="text-zinc-300">{t('admin.verified')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setUserDialogOpen(false)} className="border-white/20 text-zinc-400 hover:text-white rounded-sm">{t('common.cancel')}</Button>
              <Button onClick={handleSaveUser} disabled={saving} className="bg-white text-black hover:bg-zinc-200 rounded-sm">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('admin.saving')}</> : <><Save className="h-4 w-4 mr-2" />{t('common.save')}</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">{t('admin.createUser')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400">{t('admin.name')}</Label>
                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder={t("admin.name")} data-testid="create-user-name" />
              </div>
              <div>
                <Label className="text-zinc-400">メールアドレス</Label>
                <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="email@example.com" data-testid="create-user-email" />
              </div>
              <div>
                <Label className="text-zinc-400">{t('admin.passwordLabel')}</Label>
                <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="********" data-testid="create-user-password" />
              </div>
              <div>
                <Label className="text-zinc-400">{t('admin.role')}</Label>
                <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" data-testid="create-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1C] border-white/10">
                    <SelectItem value="user" className="text-zinc-300">{t('admin.roleUser')}</SelectItem>
                    <SelectItem value="admin" className="text-zinc-300">{t('admin.roleAdmin')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setCreateUserDialogOpen(false)} className="border-white/20 text-zinc-400 hover:text-white rounded-sm">{t('common.cancel')}</Button>
              <Button onClick={handleCreateUser} disabled={saving} className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="create-user-submit">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('admin.creating')}</> : <><UserPlus className="h-4 w-4 mr-2" />{t('admin.create')}</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* User Detail Dialog */}
        <Dialog open={userDetailDialogOpen} onOpenChange={setUserDetailDialogOpen}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">{t('admin.userDetail')}</DialogTitle>
            </DialogHeader>
            {loadingUserDetail ? (
              <div className="space-y-3 py-4">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 bg-[#1C1C1C]" />)}
              </div>
            ) : userDetail ? (
              <div className="space-y-6 mt-4" data-testid="user-detail-panel">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">{t('admin.name')}</p>
                    <p className="text-white font-medium">{userDetail.name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">{t('admin.email')}</p>
                    <p className="text-zinc-300 text-sm">{userDetail.email}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">{t('admin.role')}</p>
                    <p>{getRoleBadge(userDetail.role)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">KYC</p>
                    <p>{getKYCBadge(userDetail.is_kyc_verified, userDetail.kyc_status)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">{t('admin.status')}</p>
                    <p>{userDetail.is_active !== false ? <Badge className="bg-emerald-500/10 text-emerald-400 border-0">{t('admin.active')}</Badge> : <Badge className="bg-red-500/10 text-red-400 border-0">{t('admin.inactive')}</Badge>}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">{t('admin.registeredAt')}</p>
                    <p className="text-zinc-400 text-sm">{new Date(userDetail.created_at).toLocaleDateString('ja-JP')}</p>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-4 bg-[#0A0A0A] border border-white/10 rounded-sm text-center">
                    <Briefcase className="h-5 w-5 text-blue-400 mx-auto mb-2" />
                    <p className="text-zinc-500 text-xs mb-1">{t('admin.investCount')}</p>
                    <p className="text-white text-xl font-bold" data-testid="user-detail-invest-count">{userDetail.investment_count}</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] border border-white/10 rounded-sm text-center">
                    <DollarSign className="h-5 w-5 text-emerald-400 mx-auto mb-2" />
                    <p className="text-zinc-500 text-xs mb-1">{t('admin.totalInvested')}</p>
                    <p className="text-white text-xl font-bold">¥{(userDetail.total_invested || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0A] border border-white/10 rounded-sm text-center">
                    <ArrowDownToLine className="h-5 w-5 text-[#D4AF37] mx-auto mb-2" />
                    <p className="text-zinc-500 text-xs mb-1">{t('admin.totalDist')}</p>
                    <p className="text-white text-xl font-bold">¥{(userDetail.total_distributions || 0).toLocaleString()}</p>
                  </div>
                </div>

                {/* Investments Table */}
                {userDetail.investments && userDetail.investments.length > 0 && (
                  <div>
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4 text-blue-400" />{t('admin.investments')}</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 px-2 text-zinc-500 text-xs">{t('admin.fundCat')}</th>
                            <th className="text-right py-2 px-2 text-zinc-500 text-xs">{t('admin.amount')}</th>
                            <th className="text-right py-2 px-2 text-zinc-500 text-xs">{t('admin.currentValue')}</th>
                            <th className="text-left py-2 px-2 text-zinc-500 text-xs">{t('admin.status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetail.investments.map((inv) => (
                            <tr key={inv.id} className="border-b border-white/5">
                              <td className="py-2 px-2 text-white">{inv.fund_name}</td>
                              <td className="py-2 px-2 text-zinc-300 text-right">¥{inv.amount.toLocaleString()}</td>
                              <td className="py-2 px-2 text-zinc-300 text-right">¥{inv.current_value.toLocaleString()}</td>
                              <td className="py-2 px-2"><Badge className="bg-emerald-500/10 text-emerald-400 border-0 text-xs">{inv.status}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Distributions Table */}
                {userDetail.distributions && userDetail.distributions.length > 0 && (
                  <div>
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><ArrowDownToLine className="h-4 w-4 text-[#D4AF37]" />{t('admin.distHistory')}</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-2 px-2 text-zinc-500 text-xs">{t('admin.fundCat')}</th>
                            <th className="text-right py-2 px-2 text-zinc-500 text-xs">{t('admin.distAmount')}</th>
                            <th className="text-left py-2 px-2 text-zinc-500 text-xs">{t('admin.date')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetail.distributions.map((d) => (
                            <tr key={d.id} className="border-b border-white/5">
                              <td className="py-2 px-2 text-white">{d.fund_name}</td>
                              <td className="py-2 px-2 text-[#D4AF37] text-right">¥{d.distribution_amount.toLocaleString()}</td>
                              <td className="py-2 px-2 text-zinc-400">{d.distribution_date}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Withdrawals Table */}
                {userDetail.withdrawals && userDetail.withdrawals.length > 0 && (
                  <div>
                    <h4 className="text-white font-semibold mb-3 flex items-center gap-2"><ArrowUp className="h-4 w-4 text-orange-400" />{t('admin.withdrawHistory')}</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-right py-2 px-2 text-zinc-500 text-xs">金額</th>
                            <th className="text-left py-2 px-2 text-zinc-500 text-xs">方法</th>
                            <th className="text-left py-2 px-2 text-zinc-500 text-xs">{t('admin.status')}</th>
                            <th className="text-left py-2 px-2 text-zinc-500 text-xs">日付</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetail.withdrawals.map((w) => (
                            <tr key={w.id} className="border-b border-white/5">
                              <td className="py-2 px-2 text-white text-right">¥{w.amount.toLocaleString()}</td>
                              <td className="py-2 px-2 text-zinc-300">{w.withdrawal_method}</td>
                              <td className="py-2 px-2"><Badge className={`border-0 text-xs ${w.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' : w.status === 'rejected' ? 'bg-red-500/10 text-red-400' : 'bg-amber-500/10 text-amber-400'}`}>{w.status}</Badge></td>
                              <td className="py-2 px-2 text-zinc-400">{new Date(w.created_at).toLocaleDateString('ja-JP')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {userDetail.investment_count === 0 && (!userDetail.distributions || userDetail.distributions.length === 0) && (!userDetail.withdrawals || userDetail.withdrawals.length === 0) && (
                  <p className="text-zinc-500 text-center py-4">{t('admin.noTransactions')}</p>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">{t('admin.resetPassword')}</DialogTitle>
            </DialogHeader>
            {resetPasswordUser && (
              <div className="space-y-4 mt-4">
                <p className="text-zinc-400 text-sm">
                  <span className="text-white font-medium">{resetPasswordUser.name}</span> ({resetPasswordUser.email}) {t('admin.resetDesc')}
                </p>
                <div>
                  <Label className="text-zinc-400">{t('admin.newPassword')}</Label>
                  <div className="relative mt-1">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white rounded-sm pr-10"
                      placeholder="********"
                      data-testid="reset-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)} className="border-white/20 text-zinc-400 hover:text-white rounded-sm">{t('common.cancel')}</Button>
              <Button onClick={handleResetPassword} disabled={saving} className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="reset-password-submit">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('admin.resetting')}</> : <><KeyRound className="h-4 w-4 mr-2" />{t('admin.reset')}</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Fund Dialog */}
        <Dialog open={fundDialogOpen} onOpenChange={setFundDialogOpen}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">
                {isNewFund ? t('admin.newFund') : t('admin.editFund')}
              </DialogTitle>
            </DialogHeader>
            {editingFund && (
              <Tabs defaultValue="basic" className="mt-4">
                <TabsList className="bg-[#0A0A0A]">
                  <TabsTrigger value="basic" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-400">{t('admin.basicInfo')}</TabsTrigger>
                  <TabsTrigger value="ja" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"><FlagImg code="ja" className="w-4 h-auto inline mr-1 rounded-sm" /> {t('auth.language') === 'Language' ? 'Japanese' : '日本語'}</TabsTrigger>
                  <TabsTrigger value="en" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"><FlagImg code="en" className="w-4 h-auto inline mr-1 rounded-sm" /> English</TabsTrigger>
                  <TabsTrigger value="pt" className="data-[state=active]:bg-white data-[state=active]:text-black text-zinc-300"><FlagImg code="pt" className="w-4 h-auto inline mr-1 rounded-sm" /> Português</TabsTrigger>
                </TabsList>
                
                <TabsContent value="basic" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-400">{t('admin.targetYieldLabel')}</Label>
                      <Input type="number" step="0.1" value={editingFund.target_yield || ''} onChange={(e) => setEditingFund({ ...editingFund, target_yield: parseFloat(e.target.value) })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-400">{t('admin.actualYieldLabel')}</Label>
                      <Input type="number" step="0.1" value={editingFund.actual_yield || ''} onChange={(e) => setEditingFund({ ...editingFund, actual_yield: parseFloat(e.target.value) || null })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-400">{t('admin.minInvestLabel')}</Label>
                      <Input type="number" value={editingFund.min_investment || ''} onChange={(e) => setEditingFund({ ...editingFund, min_investment: parseFloat(e.target.value) })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-400">{t('admin.riskLevelLabel')}</Label>
                      <Select value={editingFund.risk_level} onValueChange={(value) => setEditingFund({ ...editingFund, risk_level: value })}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1C1C1C] border-white/10">
                          <SelectItem value="low" className="text-zinc-300">{t('admin.riskLow')}</SelectItem>
                          <SelectItem value="medium" className="text-zinc-300">{t('admin.riskMedium')}</SelectItem>
                          <SelectItem value="high" className="text-zinc-300">{t('admin.riskHigh')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-zinc-400">{t('admin.propertyTypeLabel')}</Label>
                      <Input value={editingFund.property_type || ''} onChange={(e) => setEditingFund({ ...editingFund, property_type: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="office, residential, commercial..." />
                    </div>
                    <div>
                      <Label className="text-zinc-400">{t('admin.locationLabel')}</Label>
                      <Input value={editingFund.location || ''} onChange={(e) => setEditingFund({ ...editingFund, location: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="Tokyo, Osaka..." />
                    </div>
                    <div>
                      <Label className="text-zinc-400">{t('admin.fundStatusLabel')}</Label>
                      <Select value={editingFund.status} onValueChange={(value) => setEditingFund({ ...editingFund, status: value })}>
                        <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1C1C1C] border-white/10">
                          <SelectItem value="active" className="text-zinc-300">{t('admin.statusPublic')}</SelectItem>
                          <SelectItem value="inactive" className="text-zinc-300">{t('admin.statusPrivate')}</SelectItem>
                          <SelectItem value="closed" className="text-zinc-300">{t('admin.statusClosed')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <input type="checkbox" id="is_guaranteed" checked={editingFund.is_guaranteed || false} onChange={(e) => setEditingFund({ ...editingFund, is_guaranteed: e.target.checked })} className="w-4 h-4" />
                      <Label htmlFor="is_guaranteed" className="text-zinc-400">{t('admin.interestGuarantee')}</Label>
                    </div>
                  </div>
                  {/* Guarantee Details Section */}
                  {editingFund.is_guaranteed && (
                    <div className="p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-sm space-y-4">
                      <div className="flex items-center gap-2 mb-2">
                        <ShieldAlert className="h-4 w-4 text-[#D4AF37]" />
                        <span className="text-[#D4AF37] font-semibold text-sm">{t('admin.guaranteeSettings')}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-400">{t('admin.guaranteeTypeLabel')}</Label>
                          <Select value={editingFund.guarantee_type || 'none'} onValueChange={(value) => setEditingFund({ ...editingFund, guarantee_type: value })}>
                            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" data-testid="guarantee-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1C1C1C] border-white/10">
                              <SelectItem value="none" className="text-zinc-300">{t('admin.guaranteeNoneOpt')}</SelectItem>
                              <SelectItem value="partial" className="text-zinc-300">{t('admin.guaranteePartialOpt')}</SelectItem>
                              <SelectItem value="full" className="text-zinc-300">{t('admin.guaranteeFullOpt')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-zinc-400">{t('admin.guaranteeRateLabel')}</Label>
                          <Input type="number" step="0.1" value={editingFund.guarantee_rate || ''} onChange={(e) => setEditingFund({ ...editingFund, guarantee_rate: parseFloat(e.target.value) || null })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder={t('admin.exampleYield')} data-testid="guarantee-rate-input" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-zinc-400">{t('admin.guaranteePeriodLabel')}</Label>
                          <Input value={editingFund.guarantee_period || ''} onChange={(e) => setEditingFund({ ...editingFund, guarantee_period: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder={t('admin.examplePeriod')} data-testid="guarantee-period-input" />
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-zinc-400">{t('admin.imageUrl')}</Label>
                    <div className="mt-1 space-y-2">
                      {editingFund.image_url && (
                        <div className="relative inline-block">
                          <img src={editingFund.image_url} alt="" className="w-32 h-20 object-cover rounded-sm border border-white/10" />
                          <button onClick={() => setEditingFund({ ...editingFund, image_url: '' })} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-500" data-testid="fund-image-remove-btn">&times;</button>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm rounded-sm border border-white/10 transition-colors" data-testid="fund-image-upload-btn">
                          {uploadingFundImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                          {uploadingFundImage ? t('admin.saving') : t('admin.logoUpload')}
                          <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif" onChange={handleFundImageUpload} className="hidden" data-testid="fund-image-file-input" />
                        </label>
                        <Input value={editingFund.image_url || ''} onChange={(e) => setEditingFund({ ...editingFund, image_url: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm text-xs flex-1" placeholder="https://..." data-testid="fund-image-url-input" />
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="ja" className="space-y-4 mt-4">
                  <div>
                    <Label className="text-zinc-400">{t('admin.fundNameLabel')}</Label>
                    <Input value={editingFund.name || ''} onChange={(e) => setEditingFund({ ...editingFund, name: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-400">{t('admin.descriptionLabel')}</Label>
                    <Textarea value={editingFund.description || ''} onChange={(e) => setEditingFund({ ...editingFund, description: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[150px]" />
                  </div>
                  {editingFund.is_guaranteed && (
                    <div>
                      <Label className="text-zinc-400">{t('admin.guaranteeConditionsLabel')}</Label>
                      <Textarea value={editingFund.guarantee_conditions || ''} onChange={(e) => setEditingFund({ ...editingFund, guarantee_conditions: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[100px]" placeholder={t('admin.guaranteeConditionsPlaceholder')} data-testid="guarantee-conditions-ja" />
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="en" className="space-y-4 mt-4">
                  <div>
                    <Label className="text-zinc-400">Fund Name</Label>
                    <Input value={editingFund.name_en || ''} onChange={(e) => setEditingFund({ ...editingFund, name_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Description</Label>
                    <Textarea value={editingFund.description_en || ''} onChange={(e) => setEditingFund({ ...editingFund, description_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[150px]" />
                  </div>
                  {editingFund.is_guaranteed && (
                    <div>
                      <Label className="text-zinc-400">Guarantee Conditions</Label>
                      <Textarea value={editingFund.guarantee_conditions_en || ''} onChange={(e) => setEditingFund({ ...editingFund, guarantee_conditions_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[100px]" placeholder="Guarantee terms and conditions..." data-testid="guarantee-conditions-en" />
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="pt" className="space-y-4 mt-4">
                  <div>
                    <Label className="text-zinc-400">Nome do Fundo</Label>
                    <Input value={editingFund.name_pt || ''} onChange={(e) => setEditingFund({ ...editingFund, name_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Descrição</Label>
                    <Textarea value={editingFund.description_pt || ''} onChange={(e) => setEditingFund({ ...editingFund, description_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[150px]" />
                  </div>
                  {editingFund.is_guaranteed && (
                    <div>
                      <Label className="text-zinc-400">Condições de Garantia</Label>
                      <Textarea value={editingFund.guarantee_conditions_pt || ''} onChange={(e) => setEditingFund({ ...editingFund, guarantee_conditions_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 min-h-[100px]" placeholder="Termos e condições da garantia..." data-testid="guarantee-conditions-pt" />
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setFundDialogOpen(false)} className="border-white/20 text-zinc-400 hover:text-white rounded-sm">{t('common.cancel')}</Button>
              <Button onClick={handleSaveFund} disabled={saving} className="bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="save-fund-button">
                {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />保存中...</> : <><Save className="h-4 w-4 mr-2" />保存</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* New Custom Page Dialog */}
        <Dialog open={newPageDialogOpen} onOpenChange={setNewPageDialogOpen}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">{t('admin.newPageCreate')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400 text-sm">{t('admin.slugLabel')}</Label>
                <Input
                  value={newPageForm.slug}
                  onChange={(e) => setNewPageForm({ ...newPageForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                  placeholder="example-page"
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1 font-mono"
                  data-testid="new-page-slug"
                />
                <p className="text-zinc-600 text-xs mt-1">URL: /page/{newPageForm.slug || '...'}</p>
              </div>
              <div>
                <Label className="text-zinc-400 text-sm"><FlagImg code="ja" className="w-4 h-auto inline mr-1 rounded-sm" /> {t('admin.titleLabel')}</Label>
                <Input value={newPageForm.title_ja} onChange={(e) => setNewPageForm({ ...newPageForm, title_ja: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" data-testid="new-page-title-ja" />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm"><FlagImg code="en" className="w-4 h-auto inline mr-1 rounded-sm" /> Title</Label>
                <Input value={newPageForm.title_en} onChange={(e) => setNewPageForm({ ...newPageForm, title_en: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm"><FlagImg code="pt" className="w-4 h-auto inline mr-1 rounded-sm" /> Título</Label>
                <Input value={newPageForm.title_pt} onChange={(e) => setNewPageForm({ ...newPageForm, title_pt: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" />
              </div>
              <Button onClick={handleCreateCustomPage} className="w-full bg-white text-black hover:bg-zinc-200 rounded-sm" data-testid="create-page-button">
                <Plus className="h-4 w-4 mr-2" />
                {t('admin.createPageBtn')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Distribution Dialog */}
        <Dialog open={distributionDialogOpen} onOpenChange={setDistributionDialogOpen}>
          <DialogContent className="bg-[#141414] border-white/10 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">{t('admin.newDistribution')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400">{t('admin.fundCat')}</Label>
                <Select value={distributionForm.fund_id} onValueChange={(value) => { setDistributionForm({ ...distributionForm, fund_id: value }); setDistributionPreview(null); }}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" data-testid="distribution-fund-select">
                    <SelectValue placeholder={t('admin.selectFund')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1C] border-white/10">
                    {funds.map((fund) => (
                      <SelectItem key={fund.id} value={fund.id} className="text-zinc-300">{fund.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">{t('admin.distributionDate')}</Label>
                <Input type="date" value={distributionForm.distribution_date} onChange={(e) => setDistributionForm({ ...distributionForm, distribution_date: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" data-testid="distribution-date" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400">{t('admin.grossRevenue')}</Label>
                  <Input type="number" value={distributionForm.gross_revenue} onChange={(e) => { setDistributionForm({ ...distributionForm, gross_revenue: e.target.value }); setDistributionPreview(null); }} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="0" data-testid="distribution-gross-revenue" />
                </div>
                <div>
                  <Label className="text-zinc-400">{t('admin.costsLabel')}</Label>
                  <Input type="number" value={distributionForm.costs} onChange={(e) => { setDistributionForm({ ...distributionForm, costs: e.target.value }); setDistributionPreview(null); }} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder="0" data-testid="distribution-costs" />
                </div>
              </div>
              <div>
                <Label className="text-zinc-400">{t('admin.memoLabel')}</Label>
                <Input value={distributionForm.notes} onChange={(e) => setDistributionForm({ ...distributionForm, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white rounded-sm mt-1" placeholder={t('admin.optional')} data-testid="distribution-notes" />
              </div>

              {/* Calculate Button */}
              <Button
                onClick={handleCalculateDistribution}
                disabled={calculating || !distributionForm.fund_id || !distributionForm.gross_revenue}
                className="w-full bg-zinc-800 text-white hover:bg-zinc-700 rounded-sm"
                data-testid="calculate-distribution-button"
              >
                {calculating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('admin.calculatingDist')}</> : <><Calculator className="h-4 w-4 mr-2" />{t('admin.previewDistribution')}</>}
              </Button>

              {/* Preview */}
              {distributionPreview && (
                <div className="p-4 bg-[#0A0A0A] rounded-sm border border-white/10 space-y-3" data-testid="distribution-preview">
                  <h4 className="text-white font-semibold">{t('admin.distributionPreview')}</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-zinc-500">{t('admin.previewFund')}</span>
                    <span className="text-white">{distributionPreview.fund_name}</span>
                    <span className="text-zinc-500">{t('admin.previewRevenue')}</span>
                    <span className="text-white">{formatCurrency(distributionPreview.gross_revenue)}</span>
                    <span className="text-zinc-500">{t('admin.previewCosts')}</span>
                    <span className="text-zinc-400">{formatCurrency(distributionPreview.costs)}</span>
                    <span className="text-zinc-500">{t('admin.previewNetProfit')}</span>
                    <span className="text-emerald-400 font-semibold">{formatCurrency(distributionPreview.net_profit)}</span>
                    <span className="text-zinc-500">{t('admin.previewInvestors')}</span>
                    <span className="text-white">{distributionPreview.investor_count}{t('admin.peopleSuffix')}</span>
                    <span className="text-zinc-500">{t('admin.previewTotal')}</span>
                    <span className="text-white font-semibold">{formatCurrency(distributionPreview.total_to_distribute)}</span>
                  </div>

                  {distributionPreview.distributions && distributionPreview.distributions.length > 0 && (
                    <div className="mt-3">
                      <p className="text-zinc-400 text-xs mb-2">{t('admin.investorBreakdown')}</p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {distributionPreview.distributions.map((d, i) => (
                          <div key={i} className="flex justify-between text-xs px-2 py-1 bg-white/5 rounded">
                            <span className="text-zinc-300">{d.user_name} ({d.share_percentage.toFixed(2)}%)</span>
                            <span className="text-white font-mono">{formatCurrency(d.distribution_amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleExecuteDistribution}
                    disabled={executing}
                    className="w-full bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm mt-3"
                    data-testid="execute-distribution-button"
                  >
                    {executing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('admin.executingDist')}</> : <><Play className="h-4 w-4 mr-2" />{t('admin.executeDistribution')}</>}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;
